// BFF client. Requests carry Telegram initData (tma) inside the Mini App,
// or the stored web-console session token (web) in a plain browser.

import { tg } from './telegram';
import { TimedOut, fetchWithin } from './within';
import { ApiError, NoAnswer } from './errors';
import { getWebSession, clearWebSession, isWebContext } from './webSession';
import { mintedLink } from './cosign-link';
import { stamped } from './readClock';

const BASE = import.meta.env.VITE_BFF_BASE_URL ?? 'https://admin-api.dubaiotc.net';
// Where the signing page is served from — fixed in the build. A build that
// names none shows no signing link, and says so (lib/cosign-link.ts).
const COSIGN_ORIGIN: string | undefined = import.meta.env.VITE_COSIGN_ORIGIN;

function authHeader(): Record<string, string> {
  const initData = tg()?.initData ?? '';
  if (initData) return { Authorization: `tma ${initData}` };
  const token = getWebSession();
  return token ? { Authorization: `web ${token}` } : {};
}

/** How long a custody request may take before the sheet treats its outcome as unknown (lib/within.ts). */
export const CUSTODY_REQUEST_MS = 30_000;

/** HOW LONG THE BOOKS MAY TAKE TO ANSWER a READ before a screen stops waiting (design system Appendix B): a read that never answers becomes a failed read
 *  ("The books did not answer in time. Try again."), never "Reading…" for ever (26 Oct 2026). The slowest real read, a big report, answers well inside
 *  45 s. A CHANGE IS NOT BOUNDED: whether pressing again is safe after "no answer" is each form's own story — a key it minted, an index or an amount it
 *  recomputes from a read that refreshes — and bounding a change would turn "Posting…" into a retryable-looking failure the forms were never built for
 *  (Astra, four rounds: the cheque's seller leg, its delivery, the inter-entity reference), and so is a GET that settles, spends or hands over a one-time answer (the sign-in poll, the settle-later
 *  sweep, the paid KYT lookup: `changes: true`). WHAT MAY BE BOUNDED IS WHAT IS HARMLESS TO ASK TWICE: a read, or a call whose only writes record what it read and are the same when
 *  asked again (the energy preview's activation record, an opened lead's wallet reading) — tests/request-timeouts lists both kinds. A call may opt in with its own `timeoutMs`; custody keeps
 *  CUSTODY_REQUEST_MS and its own uncertain-outcome handling. An object so a test can shrink them. */
export const REQUEST_LIMITS = { readMs: 45_000, custodyMs: CUSTODY_REQUEST_MS };

/** THE ONE CLIENT of the books' Worker: sign-in, the refusal in words (one ApiError), the return to the login screen when the web session has died,
 *  and the bound (REQUEST_LIMITS). Every screen reaches the Worker through it — a screen with a `fetch` of its own is a request nobody bounds (the
 *  four copies that stood in Inventory, Exchanges, Chain activity and Onboarding were removed 26 Oct 2026). `reads: true` — a POST that only
 *  reads (a preview, a search): it is bounded and worded as a read. `changes: true` — a GET that settles, spends or hands over a one-time answer (the sign-in poll
 *  consumes its nonce, the settle-later read runs the sweep, the paid KYT lookup spends a credit): it is a change, so it keeps waiting like one; the answer it carries is
 *  not asked for twice (Astra, 26 Oct). */
export async function request<T>(path: string, init: RequestInit & { timeoutMs?: number; reads?: boolean; changes?: boolean } = {}): Promise<T> {
  const custody = path.startsWith('/api/custody');
  // A custody request is bounded — the fetch AND its body, aborted together (lib/within.ts): a press that never
  // answers, or answers and then stalls, becomes an uncertain outcome the sheet can recover from, never a lock
  // with no way out (Screens' Astra reviews, 23 Oct 2026).
  const { timeoutMs, reads, changes, ...rest } = init;
  const method = String(init.method ?? 'GET').toUpperCase();
  const write = !!changes || (!reads && method !== 'GET' && method !== 'HEAD');
  let res: Awaited<ReturnType<typeof fetchWithin>>;
  try {
    res = await fetchWithin(timeoutMs ?? (custody ? REQUEST_LIMITS.custodyMs : write ? 0 : REQUEST_LIMITS.readMs), `${BASE}${path}`, {
      ...rest,
      // The custody API stands behind Cloudflare Access (P-035): the browser must
      // send Access's cookie for the API's host, which it does only with credentials.
      credentials: init.credentials ?? (custody ? 'include' : undefined),
      // ACCESS'S REDIRECT IS NOT FOLLOWED: when the custody sign-in has lapsed, Access answers every custody request with a
      // 302 to its login page, which a cross-origin fetch cannot follow. 'manual' turns it into an opaque redirect this
      // client can recognise (custody_signin below) instead of a connection error per panel.
      ...(custody ? { redirect: 'manual' as RequestRedirect } : {}),
      headers: {
        'Content-Type': 'application/json',
        ...authHeader(),
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    // The books' own screens say it in the books' words; a custody request stays the plain TimedOut its sheet reads as an UNCERTAIN outcome.
    if (e instanceof TimedOut && !custody) throw new NoAnswer(write);
    // A connection failure is NOT read as the lapsed sign-in, online or not: an outage must say it is an outage (Astra,
    // 2 Nov 2026). Only Access's own redirect (the opaque redirect below) is the sign-in.
    throw e;
  }
  // Before anything else: the lapsed custody sign-in is never the web session dying — no logout, no /login.
  if (custody && res.type === 'opaqueredirect') throw custodySignin();
  if (!res.ok) {
    const body = res.text;
    // Web session expired, or admin revoked (valid token but 403 not-an-active-admin):
    // drop the session and return to login. A plain RBAC 403 (e.g. read_only hitting
    // a write route) must NOT log the operator out.
    const sessionDead =
      res.status === 401 ||
      (res.status === 403 && body.includes('not an active admin'));
    if (sessionDead && isWebContext() && getWebSession()) {
      clearWebSession();
      window.location.assign('/login');
    }
    // The API's refusal, in words (lib/errors.ts). Screens render err.message.
    let parsed: unknown = body;
    try { parsed = JSON.parse(body); } catch { /* not JSON — keep the text */ }
    throw new ApiError(res.status, parsed as never);
  }
  return (res.text === '' ? undefined : JSON.parse(res.text)) as T;
}

function custodySignin(): ApiError {
  return new ApiError(0, { error: 'custody_signin', message: 'Custody sign-in has lapsed' });
}

/** Where he signs in to custody again: any custody read on the API's own host, opened in a tab, passes Access's sign-in. */
export const CUSTODY_SIGNIN_URL = `${BASE}/api/custody/approvals`;

export interface Me {
  telegramId: number;
  username: string | null;
  displayName: string;
  role: 'full' | 'read_only';
  entityScope: string[];
  /** The owner alone approves a payout address (the BFF's requireOwner). Absent from a server older than the rule. */
  canApprovePayouts?: boolean;
}

// Telegram Login Widget payload — forwarded verbatim to the BFF, which
// verifies the hash per Telegram docs and mints a web session token.
export interface TelegramWidgetPayload {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

// Bot-approval ("device") sign-in — the console mints a nonce, deep-links
// into the ops bot, and polls until the operator approves in Telegram.
// Both endpoints are unauthenticated; authHeader() simply resolves empty.
export interface DeviceStartResponse {
  nonce: string;
  deep_link: string;
  expires_in: number;   // seconds until the nonce expires
}

export type DevicePollStatus = 'pending' | 'approved' | 'denied' | 'expired';

export interface DevicePollResponse {
  status: DevicePollStatus;
  token?: string;   // present only when status is 'approved'
  me?: Me;          // present only when status is 'approved'
}

export type Direction = 'customer_buy' | 'customer_sell';
export interface Monetary { asset: string; amount: number }

export interface BookTradeBody {
  entity: 'dubaiotc' | 'indiaotc';
  direction: Direction;
  party_slug: string;
  party_display_name?: string;
  profile_kind?: 'tracked' | 'treasury_only';   // for lazy customer creation re-POST
  lazy_party_type?: 'counterparty';             // for lazy merchant creation re-POST
  /** Absent on an unsettled trade — nothing moves, so no drawer is named. The
   *  BFF refuses a rail when `unsettled` is set rather than ignoring it. */
  treasury_take_slug?: string;
  treasury_give_slug?: string;                  // optional when delivery_method is MC/passthrough
  /** Agreed, nothing moved yet: both legs stay on the party until a Receipt or
   *  Payment extinguishes each. Console-bookable since 2026-08-24. */
  unsettled?: boolean;
  /** SETTLE ON ACCOUNT (11 Sept 2026): only with `unsettled: true`. The trade
   *  is booked and BOTH legs are netted against the party's box in the same
   *  breath — no drawer moves and nothing waits for the sweep. It is the
   *  Convert-balance door (screens/ConvertBalance.tsx): 10,000 AED a party
   *  owes becomes the USDT they owe instead, a trade the party neither pays
   *  nor receives because the box already held the money. Same guards as any
   *  settle-later trade; the answer carries `converted: true`. */
  settle_on_account?: boolean;
  /** Convert on account: yes, convert more than the balance and flip it (409 over_balance otherwise). */
  confirm_over_balance?: boolean;
  amount_take: Monetary;
  amount_give: Monetary;
  rate: string;
  delivery_method?: 'cash' | 'bank_transfer' | 'crypto' | 'manager_cheque' | 'passthrough';
  delivery_merchant_party_slug?: string;
  delivery_fee_pct?: number;
  delivery_merchant_fee_pct?: number;
  beneficiary?: string;
  payment_method?: string;
  payment_method_detail?: string;
  notes?: string;
  deal_id?: string;
  /** Book the same TRADE again ON PURPOSE — silences only the twin-trade
   *  guard (409 repeat_suspected). Since 5 Sept 2026 it no longer covers the
   *  cross-shape guard below: one yes must not answer two different
   *  questions. */
  confirm_repeat?: boolean;
  /** The money this settled trade moves really did move a SECOND time —
   *  silences only the cross-shape guard (409 money_already_recorded: the
   *  same party moved the same asset and amount through a rail within 12 h of
   *  the trade's instant, the shape that double-booked 60,000 USDT to HK on
   *  3 Sept). Sent alongside confirm_repeat when both were confirmed. */
  confirm_money_moved_again?: boolean;
  /** Open an asset on a rail that has never held it (the BFF answers 400
   *  new_rail_asset without it, since 14 Sept 2026 — sweep item 22 — asked of
   *  a settled trade's two rails before the deal insert, as transfers.ts asks
   *  of a movement's). Book trade shows the same "open X on this rail" panel
   *  Record money does and re-posts with this set. */
  allow_new_rail_asset?: boolean;
  /** WHEN it happened, as an ISO instant — sent only when that was earlier
   *  than now. 5 Sept 2026: Loki's 3 Sept trade could not be booked because
   *  the console could only say "now", which the sweep cannot settle from
   *  3 Sept money. The BFF bounds it — not in the future, not before the books
   *  opened (1 Sept), not inside a closed month — and refuses with
   *  {field:'effective_at'} otherwise. Absent = now. Built with
   *  dubaiWallToIso(): the operator types Dubai wall-clock, never a zone. */
  effective_at?: string;
  /** A conversion on an exchange only: the first fill the figures came from,
   *  so the next fills read on that venue leaves that day's run out. */
  fills_from?: string;
  /** A conversion on an exchange only: the last fill the figures came from,
   *  so the next fills read on that venue leaves that day's run out. */
  fills_until?: string;
}

export interface TransferBody {
  /** Which desk's accounts these are. The BFF defaults expense accounts by it;
   *  the paths themselves already name it. Optional — DubaiOTC when absent. */
  entity?: 'dubaiotc' | 'indiaotc';
  source: string;
  destination: string;
  amount: Monetary;
  fee_payer: 'sender' | 'receiver' | 'house';
  fee?: Monetary;
  fee_account?: string;
  reference: string;
  notes?: string;
  /** The deal this money belongs to WITHOUT being one of its legs. The BFF
   *  stamps it on the posting as `linked_deal_id` — never `deal_id`, which
   *  would make the reversal doors refuse the transfer while trade-void never
   *  reverses it. The journal folds a row by deal_id ?? linked_deal_id.
   *  Refused unless the deal exists. (30 Sept 2026, Mechanisms — typed append
   *  for Screens' ask.) */
  linked_deal_id?: string;
  /** Part of a payment to a party that is a COST to the desk (commission,
   *  courier). `amount` stays the money that leaves the rail; the party is
   *  credited amount − cost.amount and the rest posts as an expense. Party
   *  destination only, same asset, strictly less than amount. */
  cost?: { amount: Monetary; category: string };
  /** Post the same money/trade again ON PURPOSE — the BFF answers 409 repeat_suspected without it. */
  confirm_repeat?: boolean;
  /** This money really moved a SECOND time — silences only the transfers-side
   *  cross-shape guard (409 money_already_recorded with `settled: true`, 14
   *  Sept 2026, sweep item 19: a SETTLED trade for this party already moved
   *  this asset and amount, from 12 h before the movement's instant up to
   *  now). The mirror of BookTradeBody.confirm_money_moved_again; never
   *  answered by confirm_repeat — one yes answers one question. */
  confirm_money_moved_again?: boolean;
  /** Open an asset on a rail that has never held it (the BFF answers 400
   *  new_rail_asset without it). The Record money screen asks first, in
   *  words, so the first euro into the office safe is one confirm away
   *  (operator, 12 Sept 2026: "how to?"). */
  allow_new_rail_asset?: boolean;
  /** WHEN the money moved, as an ISO instant — sent only when that was earlier
   *  than now (5 Sept 2026, same door as BookTradeBody.effective_at: a
   *  back-dated trade is settled by the sweep from money recorded around the
   *  same time, so the money must be able to carry its own time too). Same
   *  bounds, same refusals. Absent = now. */
  effective_at?: string;
}

// ── BETWEEN PARTIES (POST /api/p2p) ─────────────────────────────────────────
//
// Operator, 11 Sept 2026: a movement from one party to another where the desk
// earns nothing ("P2P, like Loki"). One party's balance moves to another party's; no
// desk account is touched — no rail, no drawer, no margin. One leg is a plain
// move; a second, RETURN leg (to → from) makes it a swap between the two
// parties at whatever terms they agreed, with the desk as bookkeeper only.
export interface P2pLeg { asset: string; amount: number }
export interface P2pBody {
  /** The FROM party's desk. */
  entity: 'dubaiotc' | 'indiaotc';
  from_slug: string;
  to_slug: string;
  /** The TO party's desk, only when it is not `entity`. A move or a swap may
   *  cross desks — the route carries an inter-desk pair per leg, in the one
   *  entry (bff routes/p2p.ts, "across desks"). */
  to_entity?: string;
  /** One or two. legs[0] goes from → to; legs[1], when present, is the return
   *  leg, to → from. */
  legs: P2pLeg[];
  /** Minted ONCE per entry with makeReference('P2P', whenIso) — the
   *  idempotency key, held across a retry like every other reference. */
  reference: string;
  notes?: string;
  /** WHEN it happened, as an ISO instant — sent only when earlier than now.
   *  The same bounded door as BookTradeBody.effective_at. */
  effective_at?: string;
  /** Post the same move again ON PURPOSE — the BFF answers 409
   *  repeat_suspected without it. */
  confirm_repeat?: boolean;
}
/** A leg as the route ANSWERS it: the asset folded, the amount the ledger's
 *  own decimal STRING (fromMinor) — not the number that was sent. */
export interface P2pLegOut { asset: string; amount: string }
export interface P2pResponse {
  ok: true;
  reference: string;
  /** Absent on a deduped answer, which carries only the reference. */
  legs?: P2pLegOut[];
  /** The reference was already in the ledger — a retry whose first attempt
   *  landed. Nothing was posted: the first posting's legs and date stand and
   *  this request's were ignored, so a screen must not report them as booked. */
  deduped?: boolean;
}

/** WHAT A CUSTOMER DEALS IN (db/0101; the operator, 27 Oct 2026: "Buyer and seller /MC /bank transfer etc also should
 *  be clearly visible on customer card in the dashboard"). From the onboarding record when the party is made, edited
 *  on the party since. Shown on the card; it changes no booking rail. */
export type TradeSide = 'buyer' | 'seller' | 'both';
export type TradeMethod = 'manager_cheque' | 'bank_transfer' | 'cash' | 'crypto' | 'angadiya';
export interface TradeProfile { side: TradeSide | null; methods: TradeMethod[] }

export interface PartySummary {
  id: number;
  slug: string;
  party_type: 'own_account' | 'customer' | 'counterparty';
  display_name: string;
  telegram_username: string | null;
  kyc_status: string;
  trust_tier: string;
  credit_limit_usd: string | number;
  treasury_slug: string | null;
  /** 'merchant' for a counterparty desk / sub-broker / peer. The list endpoint
   *  has always returned it; the type omitted it, so callers could not see the
   *  one field that says whether a staged (unsettled) trade is legitimate. */
  counterparty_role?: string | null;
  is_favorite?: boolean;   // per-operator pin; only on the list endpoint
  /** THE RUNNING ACCOUNT (admin.parties.trade_remainder_rides_on_account,
   *  db/0036). True = a trade leg of theirs on which money has moved counts as
   *  settled and the remainder rides on their balance — their own margin,
   *  settled between them and the desk separately. It is a deliberate mark on
   *  ONE party, never a tolerance and never inferred from the size of a gap.
   *  Both the list and the detail endpoint send it; optional here only so an
   *  older payload reads as "not marked" rather than as a type error. */
  trade_remainder_rides_on_account?: boolean;
  /** Live balance per asset, signed. Present only when the list was requested
   *  with `with_balances` — the endpoint reads all three account families in
   *  three calls rather than one call per party. Party-side stablecoins are
   *  already netted onto a single `USDT` key server-side; treasury rails keep
   *  their exact chain-bearing codes. */
  balances?: Record<string, number>;
  /** What they deal in — on every list row and on the detail; { side: null, methods: [] } when nothing is known. */
  trade_profile?: TradeProfile;
}

export interface PartyDetail {
  party: PartySummary & {
    entity: string;
    status: string;
    notes: string | null;
    created_at: string;
    updated_at: string;
    parent_slug?: string | null;
    parent_display_name?: string | null;
    counterparty_role?: string | null;
    profile_kind?: string | null;
  };
  balances: Record<string, number>;
  /** WHAT THIS PARTY CARRIES ON A RUNNING ACCOUNT — present only for a marked
   *  party, null for everybody else, because a heading claiming an arrangement
   *  that does not exist is a lie.
   *
   *  `balance` is NOT a second figure: the BFF derives it from the same
   *  `balances` above rather than counting the position a second way, so the
   *  two can never disagree. `rode` is the part of it that arrived by trades
   *  closing with a remainder instead of being paid — summed per asset from the
   *  settlements' own audit rows, latest row per deal, reopened trades excluded.
   *  It is the one thing the balance alone cannot tell the operator. */
  carried_on_account: {
    balance: Array<{ asset: string; amount: number }>;   // signed like the box: negative = the desk owes them
    rode: Array<{ asset: string; amount: number; trades: number }>;
  } | null;
  children?: Array<{ id: number; slug: string; display_name: string; party_type: string; treasury_slug: string | null }>;
  wallets: Array<{
    id: number;
    chain: string;
    address: string;
    label: string | null;
    risk_score: number | null;
    risk_checked_at: string | null;
    status: string;
    first_seen_at: string;
    last_seen_at: string | null;
    retired_at: string | null;
    /** When the operator archived it — no longer read (db/0064, status 'unwatched'). */
    unwatched_at?: string | null;
    /** The wallet's one name (N-091, bff/src/lib/wallet-name.ts) — `full` is "Hot · TRC20 · Nov". Absent from an older server. */
    name?: { full: string; line1: string; list: string } | null;
  }>;
  /** THE LEDGER ACCOUNT KEYS (18 Sept 2026) — a desk Bitcoin wallet is a
   *  Ledger account whose addresses derive from an extended public key kept
   *  server-side. This says only THAT the addresses come from a key, how many
   *  are registered, the last used index on each branch and when the branches
   *  were last gap-scanned. The key itself is never served. Own accounts
   *  only; empty for everybody else. */
  wallet_keys?: Array<{
    chain: string;
    scheme: string;
    gap_limit: number;
    ahead: number;
    registered: number;
    last_scanned_at: string | null;
    last_receive_used: number;
    last_change_used: number;
  }>;
  deals: DealRow[];
  /** HELD IN FLAGGED for this party, per asset (D12, 26 Oct 2026): their money on the Flagged pots, kept there until
   *  the operator releases it by hand. Information beside the balance, never part of it, and it gates nothing. */
  flagged_held?: Record<string, number>;
}

export interface DealRow {
  deal_id: string;
  direction: Direction;
  asset_take: string;
  asset_give: string;
  amount_take: string | number;
  amount_give: string | number;
  rate: string | number;
  mid_rate: string | number | null;
  spread_amount: string | number | null;
  lifecycle: 'quoted' | 'awaiting' | 'settled' | 'voided' | 'absorbed';
  payment_method: string | null;
  payment_method_detail?: string | null;
  booked_at: string;
  booked_by_admin: string;
  notes?: string | null;
  party_slug?: string;
  party_name?: string;
  party_type?: string;
}

// Row shape of GET /api/trades (bff/src/routes/trades.ts GET /).
// lifecycle is not in that SELECT yet — optional so the trades log upgrades
// in place once the BFF starts sending it.
export interface TradeRow {
  deal_id: string;
  direction: Direction;
  asset_take: string;
  asset_give: string;
  amount_take: string | number;
  amount_give: string | number;
  rate: string | number | null;
  spread_amount: string | number | null;
  payment_method: string | null;
  delivery_method: string | null;
  beneficiary?: string | null;
  booked_at: string;
  booked_by_admin: string;
  party_slug: string;
  party_name: string;
  merchant_slug?: string | null;
  merchant_name?: string | null;
  lifecycle?: string | null;
  /** How a settled trade closed ("settled by sweep 12 Sep (USDT netted · INR
   *  netted)") and anything typed under Notes — selected by the list route
   *  since 14 Sept 2026 (sweep item 59) so the deal panel's "Closed" row
   *  reads it. Absent on an older Worker. */
  notes?: string | null;
}

export interface FlowRow {
  id: number;
  reference: string | null;
  type: 'receipt' | 'payment' | null;
  timestamp: string;
  asset: string | null;
  amount: number | null;
  source: string | null;
  destination: string | null;
  payment_method: string | null;
  payment_method_detail: string | null;
  notes: string | null;
  operator: string | null;
}

// ── THE MONEY-MOVEMENT LEDGER (GET /api/movements) ──────────────────────────
//
// Operator, 4 Sept 2026: "These is no screen that shows Record in/out & Move
// in/out history + peerticular party trades/movement histroy — that should be
// there for verification."
//
// NOT `FlowRow` above, and not an extension of it. `recentFlows` is the
// Dashboard glance: one page of the newest transactions, keeping only
// metadata.type 'receipt' and 'payment' — which misses `transfer`, the shape
// Record money actually posts, and every move, adjustment, cost, capital and
// drawing besides. This is the ledger: filtered, paged, totalled, and read
// from Postgres so it can be.
export type MovementKind =
  | 'trade'
  | 'money_in' | 'money_out' | 'rail_move' | 'adjustment' | 'expense' | 'owner'
  // A balance moved from one party to another (POST /api/p2p, 11 Sept 2026).
  // No rail on the row: `party` is the FROM party, `counterparty` the TO party,
  // `amount` is the first leg.
  | 'p2p';
/** What the screen's filter offers — the kinds, plus "don't filter". */
export type MovementKindFilter = 'all' | MovementKind;
/** 'move' is one rail to another: the same money on both sides, so it counts toward
 *  neither the in nor the out total. */
export type MovementDirection = 'in' | 'out' | 'move';
/** A `reverted` row was undone; a `reversal` is the entry that undid one.
 *  Both are hidden unless the caller asks for include_undone. */
export type MovementState = 'live' | 'reverted' | 'reversal';

/** `entity` is the desk the party is on (bff lib/movements.ts PartyRef; the
 *  route sends the parties row through as it is). It matters on a `p2p` row,
 *  whose two parties may sit on different desks — a link built on the
 *  screen's desk would open the wrong one. Optional so older rows still read. */
export interface MovementParty { slug: string; display_name: string; party_type: string; entity?: string }
/** `slug` is the dotted treasury slug ('cash.office'); `label` is the
 *  operator-set display name. Pass the pair to `railName()` — never render
 *  either field raw. */
export interface MovementRail { slug: string; label: string }

export interface MovementRow {
  /** Transaction id as TEXT: a JSON number is a float by the time it lands
   *  here, and an id has to survive intact. */
  txn_id: string;
  reference: string | null;
  at: string;
  kind: MovementKind;
  /** What happened, in the desk's own words, already carrying the formatted
   *  amount, the party and the rail. Built once, server-side (bff
   *  lib/movements.ts) so this sentence cannot drift between screens. */
  words: string;
  party: MovementParty | null;
  /** The party a `p2p` move LANDED on (`party` is where it came from). Absent
   *  or null on every other kind — optional, so rows from before the kind
   *  existed read unchanged. */
  counterparty?: { slug: string; display_name: string; entity?: string } | null;
  rail: MovementRail | null;
  /** Where a move between rails landed; null on every other kind. */
  counter_rail: MovementRail | null;
  /** A TRADE's other account (17 Sept 2026): the one its second leg moved
   *  on, so the per-currency statement can name the account on the other
   *  asset's line. Null on every other kind; absent on an older Worker. */
  other_rail?: MovementRail | null;
  asset: string;
  /** Display units, ALWAYS POSITIVE — `direction` carries the sign. */
  amount: number;
  direction: MovementDirection;
  /** What this movement cost the desk: the commission inside a split payment,
   *  or the whole of a pure expense. */
  cost: { amount: number; category: string } | null;
  notes: string | null;
  operator: string | null;
  state: MovementState;
  /** THE STATEMENT COLUMNS (17 Sept 2026, the operator's Wio statement: "I
   *  need total also at each line"): one entry per asset this transaction
   *  moved on the accounts in scope — the account filter's account, else
   *  every desk account — its signed net and the balance those accounts held
   *  in that asset right after it. Empty on a railless row (between parties,
   *  a party opening, a profit share). Absent on an older Worker, and the
   *  screen then draws one row in the head asset with no Balance column. */
  legs?: MovementLeg[];
}

/** One asset's leg of a movement, as numeric::text ("912316.75") — the raw
 *  ledger asset code, folded on the console. `running` is the balance AFTER
 *  this entry over the accounts in scope; it is the accounts' own figure and
 *  ignores the kind, party and search filters. */
export interface MovementLeg { asset: string; net: string; running: string | null }

/** Over the WHOLE filtered set, never just the page — a total that changes as
 *  you scroll looks like an answer and is not one. `opening` / `closing` (17
 *  Sept 2026) are the accounts' balances at the window's two edges, as
 *  numeric::text, whatever the kind, party and search filters say; absent on
 *  an older Worker. */
export interface MovementTotal {
  asset: string; in: number; out: number; net: number;
  opening?: string; closing?: string;
}

/** Set only when a DATE WINDOW is asked for AND an entry inside it was reverted
 *  after it. Over all history a reversal cancels its entry exactly, so the
 *  totals equal the rail's balance; inside a window they need not, and the
 *  screen has to say so rather than quietly disagree with Treasury. */
export interface MovementWindowCaveat {
  rows: number;
  per_asset: Array<{ asset: string; amount: number }>;
}

export interface MovementsResponse {
  rows: MovementRow[];
  next_cursor: string | null;
  totals: MovementTotal[];
  window_caveat: MovementWindowCaveat | null;
}

/** One account an entry touched (GET /api/movements/:txn): its signed net, + into the account, as numeric text in the
 *  asset's own units; named by its party where the desk has a name (a rail, a customer, a counterparty), else null. */
export interface MovementPosting {
  account: string;
  name: string | null;
  asset: string;
  net: string;
}

/** GET /api/movements/:txn — one entry, for a deep link (26 Oct 2026). `movement` is the row the list gives it (null
 *  when the entry moved nothing the Money screen lists: a memo pair, a month's close); `postings` is every account
 *  it touched. 404 not_found when the books hold no such entry. */
export interface MovementDetail {
  movement: MovementRow | null;
  postings: MovementPosting[];
}

/** POST /api/movements/:txn/reverse — the console's door for a money entry
 *  (17 Sept 2026). `deduped` when the entry was already reversed. The books
 *  side of the reply: how many of the matcher's own chain ties were released
 *  (the Chain row is Pending again) and how many hand-made ties were left
 *  standing for the operator to decide about. */
export interface ReverseMovementResponse {
  ok: true;
  deduped?: boolean;
  txn_id: string;
  reversal_txn_id: string | number | null;
  reference: string | null;
  chain_untied: number;
  manual_ties_left: number;
}
/** The 409 `confirm` on `confirm_drawable` names these — an awaiting trade of
 *  the same party this entry could still settle. */
export interface DrawableDeal { deal_id: string; party: string; leg: 'take' | 'give'; asset: string; still_open: string }

export interface MovementsQuery {
  entity?: string;
  kind?: MovementKindFilter;
  party?: string;
  /** Dotted treasury slug, the form Treasury shows: 'cash.office'. */
  rail?: string;
  /** v2 or v3 code — the route folds it before matching. */
  asset?: string;
  /** DUBAI calendar days, YYYY-MM-DD, both inclusive. */
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
  cursor?: string;
  include_undone?: boolean;
}

// ── ONE PARTY'S STATEMENT (GET /api/parties/:slug/statement) ────────────────
//
// Everything that touched this party — movements AND trades — oldest first,
// with the running balance after each row. It exists to be checked: the last
// row in an asset IS that asset's box, so the arithmetic on screen has to
// reach the number the party page already shows.
export interface StatementRow {
  /** null on the synthetic "balance brought forward" row — no single
   *  transaction created it. */
  txn_id: string | null;
  reference: string | null;
  at: string | null;
  /** The ledger's own metadata type ('transfer', 'trade', 'opening_balance',
   *  'rebalance', 'mc_book'…), 'opening' on the brought-forward row, or
   *  'movement' when the transaction carried no type. Raw vocabulary — put it
   *  through `ledgerTypeLabel()` (lib/naming.ts) before it reaches the DOM. */
  kind: string;
  words: string;
  deal_id: string | null;
  /** A money leg's deal without being its leg (sub-broker receipt/delivery). Fold by deal_id ?? linked_deal_id. Absent on an older Worker. */
  linked_deal_id?: string | null;
  /** The desk account this leg moved on (1 Nov 2026): its rail slug ('cash.office') and its name as the Books name
   *  it ('Office cash'; 'Hot · TRC20' once db/0104 is applied). A trade's legs each name their own asset's account.
   *  Null when no desk account moved in this asset — a settle-later trade, between parties, an opening, the
   *  brought-forward row. Absent on an older Worker. */
  rail?: string | null;
  rail_name?: string | null;
  asset: string;
  /** SIGNED, display units, as it moved the party's box: POSITIVE means the
   *  party owes the desk more, negative means the desk holds more of theirs. */
  delta: number;
  /** The party's balance in this asset AFTER this row. */
  running: number;
  state: MovementState;
}

export interface PartyStatementResponse {
  party: MovementParty;
  /** Today's position — the same numbers PartyDetail's Balances section shows. */
  box: Array<{ asset: string; balance: number }>;
  /** EVERY asset that ever touched them, busiest first — including ones settled
   *  back to zero, which the box cannot carry and whose history was therefore
   *  unreachable from the screen. */
  assets: Array<{ asset: string; legs: number }>;
  /** OLDEST FIRST, so the running balance reads down the page. */
  rows: StatementRow[];
  next_cursor: string | null;
}

export interface StatementQuery {
  entity?: string;
  asset?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
  include_undone?: boolean;
}

/** One row of GET /api/cex's `proof` (db/0102 admin.exchange_proof): an exchange rail and asset with the venue's
 *  figure beside the books'. Decimal strings, display units. `proof` itself is null when it could not be read. */
export interface ExchangeProofRow {
  entity: string;
  /** The books' rail: 'exchange.bitget', 'exchange.bitget-india'. */
  rail: string;
  /** The mirror account answering the rail — null when the mirror has none. */
  venue: string | null;
  label: string | null;
  /** admin.cex_account ids answering the rail (the `account_id` of `accounts`); empty when none does. */
  account_ids: string[];
  asset: string;
  /** What the venue holds at its latest sync, every wallet summed; null when no mirror account answers the rail. */
  venue_total: string | null;
  books_total: string;
  /** venue_total − books_total: + the venue holds more than the books, − the books say more than the venue. */
  difference: string | null;
  /** The rail's latest venue reading; an old one is an old venue figure. */
  venue_as_of: string | null;
}

/** One recommendation from the advisor (GET /api/cex/advice). */
export interface CexAdviceItem {
  rule: string;
  severity: 'fix' | 'advise' | 'note';
  entity: string | null;
  subject: string;
  title: string;
  detail: string | null;
  action: string;
  book: string | null;
}

/** One of the desk's own fills on an exchange (GET /api/cex/fills), as the
 *  venue reported it and the sync mirrored it: the venue's pair ('BTC/USDC'),
 *  the DESK's side on it, exact decimal strings. Never folded — the venue's
 *  codes are the venue's. */
export interface CexFillRow {
  id: number;
  entity: string;
  venue: string;
  label: string;
  symbol: string;
  side: 'buy' | 'sell';
  base_asset: string;
  quote_asset: string;
  amount: string;
  price: string;
  cost: string;
  fee_amount: string | null;
  fee_asset: string | null;
  order_id: string | null;
  occurred_at: string;
}
/** Per (venue, pair, side, Dubai day) over EVERY fill in the window: what
 *  was bought or sold, what it cost, the cost-weighted rate, the fees by
 *  asset. One evening's run is one line — one conversion on the books.
 *  Numbers — a reading for the operator or a preset for the calculator,
 *  never a figure the books store. */
/** One unbooked line (GET /api/unbooked-trades). Amounts and the rate are exact decimal strings: round an amount for
 *  display only, never the rate (rule 10). `from` left the account, `to` came in; the rate is quote per base. */
export interface UnbookedTradeLine {
  account: { id: number; venue: string; label: string; entity: string; rail: string };
  day: string;
  symbol: string;
  side: 'buy' | 'sell';
  from: { asset: string; amount: string };
  to: { asset: string; amount: string };
  /** Null only when the line's base amount sums to zero (Mechanisms: real fills never do). */
  rate: string | null;
  fees: Record<string, string>;
  fills: number;
  firstAt: string;
  lastAt: string;
}

export interface CexFillSummary {
  venue: string;
  symbol: string;
  side: 'buy' | 'sell';
  /** The books' day the fills fell on, Dubai clock, 'YYYY-MM-DD'. */
  day: string;
  fills: number;
  amount: number;
  cost: number;
  rate: number | null;
  fees: Record<string, number>;
  first_at: string | null;
  last_at: string | null;
}

/** THE OUTSIDE READING (19 Sept 2026). The books hold nothing of this asset
 *  on this account, but the outside world shows money there: a wallet's
 *  pending chain rows (`chain`), an exchange's own balance (`venue`), above
 *  dust. The BFF decides which accounts get one (bff/src/routes/treasury.ts,
 *  Outside) and puts the account in its Treasury group at 0 with the reading
 *  beside it; every screen reads the same field. `words` are the desk words
 *  for where the money is — 'on chain, not yet booked' · 'on the venue'. The
 *  amount is never priced and never in a subtotal: it is not the books' money
 *  yet. Absent (or null) on every other row. */
export interface TreasuryOutside {
  kind: 'chain' | 'venue';
  amount: number;
  words: string;
}

export interface TreasuryRow {
  account: string;
  /** The desk's own registry says this rail may hold this asset, but nothing
   *  has been posted to it yet (a wallet's chain, admin.rail_assets for the
   *  office safe, or the closed chart's exchange boxes for a venue). Offered
   *  at zero so a first deposit needs no hand-typing. */
  known?: boolean;
  /** See TreasuryOutside — set on a known row the outside world shows money
   *  on; absent from an older Worker. */
  outside?: TreasuryOutside | null;
  /** When this account last moved (ISO), null when the books could not say. */
  last_activity_at?: string | null;
  /** A rail that may go below zero from every door (the angadiya) — a screen
   *  never refuses an amount on it for being more than the balance. Absent
   *  from an older Worker, which reads as bounded. */
  running_account?: boolean;
  /** Live transactions that moved this account in the last 30 days — the
   *  desk-wide "most used" rank a rail picker leads with when no party is in
   *  context (12 Sept 2026). 0 when nothing moved; absent from an older
   *  Worker, and the picker then partitions by activity as before. */
  uses_30d?: number;
  /** Exchange rows only: what the venue itself holds of this asset across its
   *  wallets, from the latest sync — beside what the books say, so an account
   *  at zero on the books can still be offered when the venue holds the coin.
   *  Null when the venue has no reading for the asset or the readings could
   *  not be read; absent from an older Worker. */
  venue_balance?: number | null;
  /** Exchange rows only: the resolved (venue, account label) pair GET
   *  /api/cex/fills needs to answer for THIS rail and no other — pass both
   *  straight through to `cexFills()`. One venue can hold more than one
   *  account under a desk now (the IndiaOTC wind-down put `exchange.bitget`
   *  and `exchange.bitget-india` on dubaiotc together), so the venue word
   *  alone stopped being a unique account; `label` (bff/src/lib/cex-rail.ts,
   *  the same rule the fills route filters on) is the discriminator. Absent
   *  on every non-exchange row, and on an exchange row whose account could
   *  not be resolved — that absence means STOP, not "ask for the venue
   *  alone": a venue-only fills read answers for whichever account matches
   *  first, which is the wrong-account bug this field exists to end. */
  venue_account?: { venue: string; label: string } | null;
  /** Operator-set display name (admin.parties.display_name); preferred label. */
  label?: string | null;
  slug?: string;
  treasuryKind?: string;
  provider?: string;
  persona?: string;
  category?: 'fiat' | 'crypto';
  asset?: string;
  balance: number;
}

/** One rail a party's money has moved through (GET /api/parties/:slug/rails):
 *  the v3 account address, how many live transactions carried both the party
 *  and this rail, and the newest of them. Busiest first, newest first among
 *  equals. Any desk's rail — matched to picker rows by `account`. */
export interface PartyRail {
  account: string;
  uses: number;
  last_at: string;
}
/** GET /api/parties/:slug/rails. `last_direction` (14 Sept 2026, sweep item
 *  14): which way the desk LAST traded with this party — the latest non-voided
 *  deal in admin.deals — so Book trade opens its toggle on it; null for a
 *  party with no trade yet (the screen then reads Customer buys). Rides the
 *  rails answer so the screen learns both in one round trip. */
export interface PartyRailsResponse {
  rails: PartyRail[];
  last_direction?: Direction | null;
}

/** GET /api/trades/median — the desk's OWN dealt rate for one oriented pair
 *  and direction over the last 24 hours (sweep item 25): a median, as the
 *  balance sheet prices by, never a mean. `median` is null when the desk has
 *  not dealt the pair that way in the window. */
export interface TradeMedian {
  pair: string;
  direction: Direction;
  window_hours: number;
  trades: number;
  median: number | null;
  low: number | null;
  high: number | null;
}

/* ── Valuation, shared by Treasury and the balance sheet ───────────────────
 *
 * Both screens now put a DERIVED figure beside a counted one, and both are
 * priced by one lib on the worker (bff/src/lib/valuations.ts). These types are
 * that lib's output as it reaches the client, named once here so the two
 * screens cannot describe the same rate two ways.
 */

/** How a figure was arrived at. Evidence-first: everything above `stated` is
 *  something the desk actually DID; `stated` is something somebody SAID.
 *  `naming.ts::valuationBasisName()` turns it into words. */
export type ValuationBasis =
  | 'identity' | 'peg' | 'cross' | 'dealt' | 'dealt_cross' | 'family' | 'stated';

/** One row of `admin.asset_valuations` that a read actually leaned on: a price
 *  somebody named, the date it was true, and how old that makes it now. */
export interface StatedValuation {
  asset: string;
  quote: string;
  rate: number;
  /** ISO instant the price was TRUE — not when the row was written. */
  as_of: string;
  source: string;
  note: string | null;
  age_hours: number;
}

/* ── Treasury: the operator's grouped view ─────────────────────────────────
 * These were typed inside TreasuryBalances.tsx while the shape was settling.
 * They live here now because the roll-up made a second consumer of them.
 */
export interface TreasuryGroupSource {
  label: string | null;
  slug: string;
  account: string;
  balance: number;
  /** Absent when the rate book could not be read; null when this desk cannot
   *  price the asset at all. NEVER render either as 0. */
  value_usdt?: number | null;
  /** See TreasuryOutside — balance 0, money waiting outside the books. */
  outside?: TreasuryOutside | null;
}

export interface TreasuryGroupLine {
  key: string;
  label: string | null;
  asset?: string;
  balance?: number;
  /** Valuation rate stamped into posting metadata (display-layer only). */
  rate?: string;
  rate_quote?: string;
  sources?: TreasuryGroupSource[];
  slug?: string;
  balances?: Array<{ asset: string; balance: number; account: string; value_usdt?: number | null; outside?: TreasuryOutside | null }>;
  /** See TreasuryOutside. Per (account, asset), so the BFF stamps it on the
   *  entries — `balances[]` here, `sources[]` on an asset line — never on the
   *  line itself today; screens read the entries. */
  outside?: TreasuryOutside | null;
  /** A rail the desk draws on and settles later (the angadiya), whose balance
   *  legitimately runs NEGATIVE — that is money the desk owes it, not an error.
   *  Decided by the BFF (lib/buckets.ts RUNNING_ACCOUNT_RAILS), the same rule
   *  the Sheet files "Owed to running accounts" by. Absent on an older worker,
   *  which reads as false — the previous rendering, never a hidden debt. */
  running_account?: boolean;
}

/** One asset held on one rail, with what it is worth in USDT. */
export interface TreasuryRailAsset {
  asset: string;
  balance: number;
  /** null = this desk cannot price this asset. Show the quantity and say so. */
  value_usdt: number | null;
  basis: ValuationBasis | null;
  /** See TreasuryOutside — this coin sits at 0 on the books with money
   *  waiting outside; its value_usdt is 0 (a count), the reading unpriced. */
  outside?: TreasuryOutside | null;
}

/** The cumulative value of everything ONE rail holds — the roll-up the Treasury
 *  screen collapses a multi-coin wallet into (operator, 4 Sept 2026). */
export interface TreasuryRailValue {
  slug: string;
  label: string | null;
  group: string;
  /** null when at least one asset on the rail could not be priced — a partial
   *  sum that looks complete under-reports a wallet with a straight face.
   *  `value_usdt_priced` carries the part that IS known, for a screen willing
   *  to say "at least X, plus the ones we have no price for". */
  value_usdt: number | null;
  value_usdt_priced: number;
  assets: TreasuryRailAsset[];
  unpriced: string[];
  /** Non-empty when any part of this figure came from a stated price. The
   *  screen must then label the figure as an estimate and date it. */
  stated_sources: string[];
  /** OLDEST stated price feeding this rail — the age that matters. */
  stated_as_of: string | null;
  stated_age_hours: number | null;
}

export interface TreasuryGroup {
  id: string;
  label: string;
  style: 'asset_lines' | 'rail_lines';
  lines: TreasuryGroupLine[];
  subtotals: Record<string, number>;
  rails?: TreasuryRailValue[];
}

/** How to read every `value_usdt` in the response. The screen must not print a
 *  roll-up without it: `available: false` means the rate book could not be read
 *  at all, and nothing may then render as 0 or as blank. */
export interface TreasuryValuation {
  quote: string;
  basis: string;
  usd_aed_peg: number;
  available: boolean;
  stated_available: boolean;
  error?: string;
  stated: StatedValuation[];
  /** Assets on this desk nobody can price — neither dealt nor stated. */
  unpriced: string[];
}

export interface TreasuryBalancesResponse {
  entity: string;
  totals: Record<string, number>;
  kindTotals: Record<string, Record<string, number>>;
  rows: TreasuryRow[];
  /** Absent while the deployed worker predates the grouped response — the
   *  screen falls back to its by-kind layout rather than blanking. */
  groups?: TreasuryGroup[];
  pocket_totals?: Record<string, number>;
  /** Acquisition-cost accounts. Not pockets; not rendered on Treasury. */
  balance_sheet?: { label: string; lines: Array<{ label: string | null; slug: string; account: string; asset: string; balance: number }> };
  valuation?: TreasuryValuation;
}

// ── Expenses + owner equity (chart of accounts §5 and §3) ────────────────────
// The console never sends an account path: it sends a category token and the
// BFF resolves it. `account` below is documentation of the agreed mapping —
// it is the contract these tokens are checked against, and it is never
// rendered (chart paths are jargon; the label is what reaches the screen).
// The token is USUALLY the chart tail, but not always: the posting funnel folds
// three P&L tails on the way into books-v3 (bff/src/lib/v3translate PL_TAIL), so
// `network_fee` lands on `expense:{entity}:network`. The `account` column below
// records where each token actually ends up — the BFF derives the same answer
// through translateAccount(), so the two cannot drift.
//
// 3 Sept 2026: 'general' was renamed 'other' — it had no account in the closed
// chart, so every posting under it came back a chart violation. The legacy
// buckets went with the mirror era, which ended 1 September. The BFF keeps one
// alias, general → other (expenses.ts CATEGORY_ALIASES), so a console bundle
// still cached in a phone posts to a real account instead of failing.
export type ExpenseCategory =
  | 'travel'
  | 'angadiya'
  | 'salary'
  | 'office:rental'
  | 'office'
  | 'network_fee'
  | 'bank'
  | 'professional'
  | 'other';

export const EXPENSE_CATEGORIES: ReadonlyArray<{
  value: ExpenseCategory;
  label: string;
  account: string;
}> = [
  // Nine heads, as few as hold the desk's costs (operator, 21 Sept 2026),
  // most frequent first. scripts/check-expense-categories.mjs holds this
  // list, the Worker's, the P&L lines and the chart to one set.
  { value: 'travel',         label: 'Travel & transport',             account: 'expense:dubaiotc:travel' },
  { value: 'angadiya',       label: 'Angadiya',                       account: 'expense:dubaiotc:angadiya' },
  { value: 'salary',         label: 'Salaries & wages',               account: 'expense:dubaiotc:salary' },
  { value: 'office:rental',  label: 'Rent',                           account: 'expense:dubaiotc:office:rental' },
  { value: 'office',         label: 'Office & admin',                 account: 'expense:dubaiotc:office' },
  { value: 'network_fee',    label: 'Network fee',                    account: 'expense:dubaiotc:network' },
  { value: 'bank',           label: 'Bank & card charges',            account: 'expense:dubaiotc:bank' },
  { value: 'professional',   label: 'Legal, licence & professional',  account: 'expense:dubaiotc:professional' },
  { value: 'other',          label: 'Other',                          account: 'expense:dubaiotc:other' },
];

/** `treasury_slug` is the dotted rail slug ('cash.office'), matching the
 *  manager-cheque routes — not a Formance path. The asset rides in `amount`,
 *  so a rail and its currency are always agreed on by construction. */
export interface ExpenseBody {
  /** The desk account that paid. Leave out when a PARTY paid (paid_by_party_slug). */
  treasury_slug?: string;
  /** A party paid this cost for the desk: the cost is recognised and the desk
   *  owes them that much more; no desk account moves (11 Sept 2026, JP Office). */
  paid_by_party_slug?: string;
  amount: Monetary;
  category: ExpenseCategory;
  reference: string;
  notes?: string;
  /** Optional attribution: the party the money went to. The cost still lands
   *  in `category` — without this, commissions and courier costs disappear
   *  into party balances and profit reads high (chart §5). */
  party_slug?: string;
}

/** One expense posting as GET /api/expenses returns it. The list is built from
 *  the ledger by DESTINATION prefix, so it also catches expense legs embedded
 *  in trades, transfers and swaps (a skimmed network fee) — those come back
 *  with `standalone: false`. Amounts are display units. */
export interface ExpenseRow {
  id: number;
  reference: string | null;
  timestamp: string;
  category: string;            // chart tail: 'office', 'office:rental', 'other', …
  account: string;             // full expense path — not for display
  paid_from: string;           // the rail the cost came out of
  asset: string;
  amount: number;
  amount_minor: number;        // raw ledger integer — audit-exact
  standalone: boolean;         // true = booked via the Record expense form
  tx_type: string | null;
  party_slug: string | null;
  treasury_slug: string | null;
  notes: string | null;
  operator: string | null;
}

export interface ExpenseListResponse {
  from: string | null;
  to: string | null;
  expenses: ExpenseRow[];
  totals: Record<string, number>;
  totals_minor: Record<string, number>;
  count: number;
  returned: number;
  scanned: number;
  /** true → the scan ran out before reaching `from`; totals are PARTIAL. */
  truncated: boolean;
}

/** Capital in and drawings out carry no category and no party — they are the
 *  owner's own money moving, not a cost and not a trade. */
export interface EquityMoveBody {
  treasury_slug: string;
  amount: Monetary;
  reference: string;
  notes?: string;
}

// ── THE DESK'S PROFIT SHARE (POST /api/expenses/profit-share) ───────────────
//
// Operator, 17 Sept 2026, after paying Loki OTC his 50,000 INR cash half:
// "another 50k to be entered as desk profit out of loki account as profit
// sharing — loki INR should decrease and desk profit to be increased." The
// desk's share of a sub-broker's balance: the party's box falls by the amount
// with no cash moving, and the sub-broker cost line falls by the same
// (LEDGER-STANDARD §2.7 — expense:{e}:subbroker → the party's box). Refused
// in words when the desk holds less of the party's money in that asset than
// the share.
export interface ProfitShareBody {
  /** The party's desk. DubaiOTC when absent. */
  entity?: 'dubaiotc' | 'indiaotc';
  party_slug: string;
  amount: Monetary;
  /** Minted ONCE per entry with makeReference('PSH', whenIso) — the
   *  idempotency key, held across a retry like every other reference. */
  reference: string;
  notes?: string;
  /** WHEN it happened, as an ISO instant — sent only when earlier than now.
   *  The same bounded door as BookTradeBody.effective_at. */
  effective_at?: string;
}
export interface ProfitShareResponse {
  ok: true;
  reference: string;
  /** The party's box in that asset after the posting (the ledger's own
   *  decimal string; negative = the desk holds theirs). Absent on a deduped
   *  answer, which carries only the reference. */
  party_after?: { asset: string; balance: string };
  /** The reference was already in the ledger — a retry whose first attempt
   *  landed. Nothing was posted on THIS request. */
  deduped?: boolean;
}

// ── A DELIVERY CHARGE (POST /api/charges) ───────────────────────────────────
//
// Operator, 2 Oct 2026: "Delivery Charges we take in from customers,
// sometimes in USDT, sometimes in cash AED - Prepare a profit account for that
// and mechanism to record those things, we charge that for MC delivery as
// well." Its own entry and its own profit line ("Delivery charges"), never a
// field in the trade or cheque forms (bff lib/charge.ts has the design). The
// charge is in the desk's MONEY; one taken in coin is sent as the AED figure
// with the coin and the rate beside it, and the screen receives the coins
// (transfer) and buys them into the cost book (bookTrade, settle_on_account)
// as their own entries, in that order. Refused in words, like every door.
export interface ChargeBody {
  /** The party's desk. DubaiOTC when absent. */
  entity?: 'dubaiotc' | 'indiaotc';
  party_slug: string;
  /** What the charge is for. 'delivery' when absent — the only kind today. */
  kind?: 'delivery';
  /** The charge, in money: AED, INR, USD or EUR. Never a coin — the BFF
   *  answers 400 charge_in_coin with what to send instead. */
  amount: Monetary;
  /** The rail's dot-form slug (`cash.office`). PRESENT = paid now, into that
   *  rail, in the same entry. ABSENT = it stays on the party's account. */
  paid_into_slug?: string;
  /** The trade or cheque this charge belongs to, without being its leg — the
   *  charge stays its own entry and is reversed on its own. */
  linked_deal_id?: string;
  /** What it was taken in, when in coin: stamped on the entry, and the BFF
   *  holds `amount` to it to the fils (400 coin_value_mismatch otherwise). */
  coin?: { asset: string; amount: number; rate: string };
  /** Minted ONCE per entry with makeReference('CHG', whenIso) — the
   *  idempotency key, held across a retry like every other reference. */
  reference: string;
  notes?: string;
  /** WHEN it happened, as an ISO instant — sent only when earlier than now. */
  effective_at?: string;
  /** "Yes, the same charge again on purpose" — answers 409 repeat_suspected. */
  confirm_repeat?: boolean;
}
export interface ChargeResponse {
  ok: true;
  reference: string;
  /** The reference was already in the ledger — a retry whose first attempt
   *  landed. Nothing was posted on THIS request. */
  deduped?: boolean;
  transaction?: unknown;
}

// ── Profit and loss (GET /api/summary/pnl) ───────────────────────────────────
// Canonical shape documented above the route in bff/src/routes/summary.ts.
// Every figure is per asset and already display-unit; nothing is FX-converted,
// so there is deliberately no single profit number.
export type PnlPeriodKey = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all' | 'custom';
export type PnlAmounts = Record<string, number>;

export interface PnlLine {
  key: string;            // stable id ('spread', 'office', …) — never shown
  label: string;          // sentence case, ready to render
  amounts: PnlAmounts;    // this period
  lifetime: PnlAmounts;   // since the books opened
  accounts: string[];     // ledger accounts folded into this line
  shared: boolean;        // account carries no entity segment (both desks)
  caveat?: string;        // quiet note to render beside the line
}
export interface PnlSection {
  lines: PnlLine[];
  totals: PnlAmounts;
  lifetime_totals: PnlAmounts;
}
export interface PnlNote { code: string; tone: 'warn' | 'info'; message: string }

export interface PnlResponse {
  entity: string;
  period: { key: PnlPeriodKey; label: string; from: string | null; to: string; timezone: string };
  assets: string[];                     // display order for columns
  income: PnlSection;
  expenses: PnlSection;
  profit: PnlAmounts;                   // income − expenses, per asset
  /** Owner money. NEVER folded into profit: a drawing is profit already
   *  earned and taken out, not a cost that reduces it. */
  owner: {
    capital_in: PnlSection;
    drawings: PnlSection;
    other: PnlSection;                  // retained / opening, when non-zero
  };
  notes: PnlNote[];
  meta: {
    source: 'transactions' | 'account_volumes';
    transactions_scanned: number;
    transactions_in_period: number;
    truncated: boolean;                 // page cap hit — numbers are partial
    minor_units: boolean;
    trades_in_period: number | null;    // admin.deals, null when unavailable
    spread_booked_in_deals: number | null;
  };
}



/** GET /api/parties: the rows, whether a balance list could not be read (with_balances only), whether the roster went past the page. */
export interface PartyListAnswer { parties: PartySummary[]; balances_read?: boolean; more?: boolean }

/** A seller payment the ledger holds that the cheque's meter never counted (GET /api/trades/mc/:id → mc_uncounted_legs). Entries posted before 26 Oct carry no
 *  rail, asset or stage: those read null and cannot be sent again from the screen. */
export interface McUncountedLeg {
  reference: string;
  leg_key: string | null;
  leg_index: number | null;
  desk_treasury_slug: string | null;
  desk_pay_asset: string | null;
  desk_pay_amount: number;
  rate_to_aed: number | null;
  complete: boolean | null;
  at: string;
}
/** A seller payment the cheque's meter has counted (GET /api/trades/mc/:id → mc_counted_legs). */
export interface McCountedLeg { leg_key: string | null; leg_index: number | null }
/** A delivery press as the books recorded it (mc_delivery_started.press). */
export interface McDeliveryPress {
  customer_balance_amount: number;
  customer_pay_asset: string;
  customer_treasury_slug: string;
  rate_to_aed: number | null;
  customer_fee_bps: number;
}
/** What a cheque's delivery has put in the ledger so far. `press` is null for entries posted before 26 Oct, which record only the balance. */
export interface McDeliveryStarted {
  references: string[];
  press: McDeliveryPress | null;
  balance: { amount: number; asset: string } | null;
}

export const api = {
  me: () => request<Me>('/api/me'),

  // Web console sign-in — widget payload in, session token + me out.
  authTelegramWidget: (payload: TelegramWidgetPayload) =>
    request<{ token: string; me: Me }>('/api/auth/telegram-widget', { method: 'POST', body: JSON.stringify(payload) }),

  // Bot-approval sign-in — start mints a nonce + deep link; poll every couple
  // of seconds until 'approved' (token + me) or a terminal 'denied'/'expired'.
  authDeviceStart: () =>
    request<DeviceStartResponse>('/api/auth/device-start', { method: 'POST' }),
  authDevicePoll: (nonce: string) =>
    request<DevicePollResponse>(`/api/auth/device-poll?nonce=${encodeURIComponent(nonce)}`, { changes: true }),

  // Parties
  listParties: (params: { entity?: string; q?: string; type?: string; is_group?: boolean;
                         with_balances?: boolean; pageSize?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.entity)   qs.set('entity', params.entity);
    if (params.q)        qs.set('q', params.q);
    if (params.type)     qs.set('type', params.type);
    if (params.is_group) qs.set('is_group', 'true');
    if (params.with_balances) qs.set('with_balances', 'true');
    // The endpoint defaults to 100 and the desk has 134 active parties, so a
    // caller that wants THE LIST must say so — the default silently truncated
    // the All tab by 34 rows, and a short list looks exactly like a complete one.
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    // `balances_read: false` (with with_balances only): a balance list could not be read, so the empty balances are
    // NOT a square desk — say "not read".
    // `more: true` (Mechanisms, 23 Oct): the roster went past its page — the rows are the FIRST ones, not all of them.
    return request<PartyListAnswer>(`/api/parties${qs.toString() ? `?${qs}` : ''}`);
  },
  promoteParty: (slug: string, reason?: string, entity = 'dubaiotc') =>
    request<{ ok: true; party: { slug: string; party_type: string; counterparty_role: string | null }; moved: string[] }>(
      `/api/parties/${encodeURIComponent(slug)}/promote?entity=${entity}`,
      { method: 'POST', body: JSON.stringify({ reason }) }),

  partyDetail: (slug: string, entity = 'dubaiotc') =>
    request<PartyDetail>(`/api/parties/${encodeURIComponent(slug)}?entity=${entity}`),

  // Per-operator pin. Entity travels in the query string, same as wallets.
  favoriteParty: (slug: string, favorite: boolean, entity = 'dubaiotc') =>
    request<{ slug: string; is_favorite: boolean }>(
      `/api/parties/${encodeURIComponent(slug)}/favorite?entity=${entity}`,
      { method: 'POST', body: JSON.stringify({ favorite }) },
    ),

  createParty: (body: {
    entity?: 'dubaiotc' | 'indiaotc';
    slug: string;
    party_type: 'customer' | 'counterparty' | 'own_account';
    display_name: string;
    telegram_username?: string | null;
    treasury_slug?: string | null;
    parent_party_slug?: string | null;
    counterparty_role?: 'merchant' | 'other';
    counterparty_capabilities?: string[];
    profile_kind?: 'tracked' | 'treasury_only';
    trust_tier?: 'new' | 'standard' | 'repeat' | 'vip';
    credit_limit_usd?: number | null;
    notes?: string | null;
    /** WHAT A NEW CASH OR BANK ACCOUNT MAY HOLD (14 Sept 2026): the fiat
     *  codes the New party form's "May hold" chips were set to. The BFF
     *  writes one admin.rail_assets row per code in the same transaction as
     *  the party, so the rail is offered at zero in every picker from its
     *  first minute (registry §3 "Fiat rails and what they may hold").
     *  own_account rails only; wallets keep admin.wallet_chain_assets. */
    assets?: string[];
    /** What they deal in (db/0101) — optional at creation. */
    trade_profile?: Partial<TradeProfile>;
  }) => request<{ party: PartySummary & { id: number; created_at: string } }>(
    '/api/parties', { method: 'POST', body: JSON.stringify(body) },
  ),

  patchParty: (slug: string, body: Partial<{
    entity: 'dubaiotc' | 'indiaotc';
    display_name: string;
    telegram_username: string | null;
    treasury_slug: string | null;
    parent_party_slug: string | null;
    counterparty_role: string | null;
    counterparty_capabilities: string[];
    profile_kind: 'tracked' | 'treasury_only';
    trust_tier: string | null;
    credit_limit_usd: number | null;
    kyc_status: string;
    notes: string | null;
    status: 'active' | 'inactive' | 'archived';
    /** Mark (or unmark) this party as carrying a running account — see
     *  PartySummary above. The route audits every flip, because it changes
     *  when this party's trades close and that is a decision, not a
     *  preference. Only the party's own page sends it. */
    trade_remainder_rides_on_account: boolean;
    /** What they deal in (db/0101): the parts sent change, the rest stay. */
    trade_profile: Partial<TradeProfile>;
  }>) => request<{ party: PartySummary }>(
    `/api/parties/${encodeURIComponent(slug)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  ),

  // Treasury
  treasuryBalances: (entity = 'dubaiotc') =>
    request<TreasuryBalancesResponse>(`/api/treasury/balances?entity=${entity}`),

  /** The desk's own fills on an exchange (15 Sept 2026): rows newest first
   *  and the per-(venue, pair, side, Dubai day) summary over the whole
   *  window. `from` / `to` are ISO instants; absent `from` = the last 3 days.
   *  `symbol` is the venue's pair, base/quote ('BTC/USDC'). `afterConversion`
   *  leaves out every fill a booked conversion on that venue already used;
   *  `booked` says how many such windows were left out.
   *
   *  `label` NAMES ONE ACCOUNT AT THAT VENUE (25 Sept 2026) — pass
   *  `TreasuryRow.venue_account.venue` / `.label` together, never `venue`
   *  alone once a rail carries `venue_account`: a venue can hold more than
   *  one account under one desk since the IndiaOTC wind-down, and `venue`
   *  by itself now names an ambiguous pair of accounts, not one. */
  /** THE UNBOOKED TRADES (Mechanisms 0e68e1f0, his "yes, build one list of unbooked trades, show it into convert home
   *  screen", 4 Nov 2026): every desk exchange account's fills no booked conversion used, one line per account · Dubai
   *  day · pair · side, newest day first. `days` 1–90. */
  unbookedTrades: (days = 30) =>
    request<{ days: number; from: string; lines: UnbookedTradeLine[] }>(`/api/unbooked-trades?days=${days}`),
  cexFills: (params: { entity?: string; venue?: string; label?: string; symbol?: string; from?: string; to?: string; limit?: number; afterConversion?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.afterConversion) qs.set('after_conversion', '1');
    if (params.entity) qs.set('entity', params.entity);
    if (params.venue)  qs.set('venue', params.venue);
    if (params.label)  qs.set('label', params.label);
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.from)   qs.set('from', params.from);
    if (params.to)     qs.set('to', params.to);
    if (params.limit)  qs.set('limit', String(params.limit));
    const q = qs.toString();
    return request<{ rows: CexFillRow[]; summary: CexFillSummary[]; from: string | null; to: string | null; booked: number }>(`/api/cex/fills${q ? `?${q}` : ''}`);
  },
  /** THE ADVISOR (22 Sept 2026): what to do next on the exchange accounts —
   *  one row per recommendation from admin.cex_advice(), fix first. `subject`
   *  is stable across reads (an account, an entity, a movement) so a list can
   *  key on it; `book` is the rule-book section. Words only: the terminal's
   *  command for each stays in scripts/cex-advise.mjs. */
  cexAdvice: () =>
    request<{ items: CexAdviceItem[]; counts: { fix: number; advise: number; note: number } }>('/api/cex/advice'),
  treasurySlugs: (entity = 'dubaiotc') =>
    request<{ entity: string; groups: Array<{ kind: string; slugs: string[] }> }>(`/api/treasury/slugs?entity=${entity}`),

  // Deals
  listTrades: (entity = 'dubaiotc', pageSize = 200) =>
    request<{ trades: TradeRow[] }>(`/api/trades?entity=${entity}&pageSize=${pageSize}`),
  /** A form's Last 10 (main 48ae76dc): the newest `pageSize` trades that match — a party, a direction, a pair (both codes
   *  or neither; either way round, folded by the Worker) — voided and pre-cutover replays left out. A Worker before
   *  48ae76dc ignores the filters and answers the desk's newest; LastTen checks every row against them. */
  lastTrades: (entity = 'dubaiotc', f: { party?: string; direction?: Direction; base?: string; quote?: string; pageSize?: number } = {}) => {
    const qs = new URLSearchParams({ entity, pageSize: String(f.pageSize ?? 10) });
    if (f.party) qs.set('party', f.party);
    if (f.direction) qs.set('direction', f.direction);
    if (f.base && f.quote) { qs.set('base', f.base); qs.set('quote', f.quote); }
    return request<{ trades: TradeRow[] }>(`/api/trades?${qs}`);
  },
  recentDeals: (entity = 'dubaiotc', limit = 20) =>
    request<{ entity: string; deals: DealRow[] }>(`/api/deals/recent?entity=${entity}&limit=${limit}`),
  recentFlows: (limit = 10) =>
    request<{ flows: FlowRow[] }>(`/api/deals/recent-flows?limit=${limit}`),

  /* ── The money-movement ledger ──────────────────────────────────────────
   * Everything Record money and Move money put in the books, plus costs,
   * adjustments, capital and drawings. Trades are NOT here — /api/trades is
   * the trades log, and this is the other history.
   *
   * Every param is optional and an omitted one is left OFF the query string
   * entirely: the route reads `?asset=` as "the empty asset" rather than
   * "any asset", and an empty history is the most expensive wrong answer this
   * screen can give. `include_undone` is only ever sent as the literal 'true'
   * the route tests for. */
  /** One entry by its transaction number — the row and its postings (MovementDetail). */
  movement: (txn: string, entity = 'dubaiotc') =>
    request<MovementDetail>(`/api/movements/${encodeURIComponent(txn)}?entity=${entity}`),

  movements: (params: MovementsQuery = {}) => {
    const qs = new URLSearchParams();
    qs.set('entity', params.entity ?? 'dubaiotc');
    if (params.kind && params.kind !== 'all') qs.set('kind', params.kind);
    if (params.party)  qs.set('party', params.party);
    if (params.rail)   qs.set('rail', params.rail);
    if (params.asset)  qs.set('asset', params.asset);
    if (params.from)   qs.set('from', params.from);
    if (params.to)     qs.set('to', params.to);
    if (params.q)      qs.set('q', params.q);
    if (params.limit)  qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.include_undone) qs.set('include_undone', 'true');
    return request<MovementsResponse>(`/api/movements?${qs}`);
  },

  /** Reverse ONE money entry — a receipt, payment, move, adjustment, cost or
   *  owner entry — by its ledger transaction id, with a reason. The route
   *  carries scripts/revert-tx.sh's guards: it refuses a trade posting (Void
   *  on the Trades log is that door), an entry a settled trade drew on
   *  (`drawn_by_deal` — reopen the trade on Settle first), and asks once
   *  (`confirm` on `confirm_drawable`) when an awaiting trade could still
   *  settle from it. The matcher's chain tie is released in the same call. */
  reverseMovement: (txnId: string, body: { reason: string; confirm_drawable?: boolean }) =>
    request<ReverseMovementResponse>(`/api/movements/${encodeURIComponent(txnId)}/reverse`,
      { method: 'POST', body: JSON.stringify(body) }),

  /** One party, everything that touched them, oldest first with a running
   *  balance that ties to their box. Lives under /api/parties because it must
   *  agree with the balances `partyDetail` serves. */
  partyStatement: (slug: string, params: StatementQuery = {}) => {
    const qs = new URLSearchParams();
    qs.set('entity', params.entity ?? 'dubaiotc');
    if (params.asset)  qs.set('asset', params.asset);
    if (params.from)   qs.set('from', params.from);
    if (params.to)     qs.set('to', params.to);
    if (params.limit)  qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.include_undone) qs.set('include_undone', 'true');
    return request<PartyStatementResponse>(
      `/api/parties/${encodeURIComponent(slug)}/statement?${qs}`);
  },

  /** The rails this party's money actually moves through, busiest first —
   *  what every rail picker leads with once a party is in context (operator,
   *  12 Sept 2026: "intelligently customised for that party"). Entity is the
   *  PARTY's desk; the rails returned may belong to either desk. Never fails
   *  the form: a DB problem answers `rails: []`. */
  partyRails: (slug: string, entity = 'dubaiotc') =>
    request<PartyRailsResponse>(`/api/parties/${encodeURIComponent(slug)}/rails?entity=${entity}`),

  /** The desk's dealt median for a pair and direction over 24 h (sweep item
   *  25) — Book trade says so when a typed rate sits more than 1 % away from
   *  it. Read only; a failure just means no line. */
  tradeMedian: (params: { entity: 'dubaiotc' | 'indiaotc'; base: string; quote: string; direction: Direction }) => {
    const qs = new URLSearchParams({ entity: params.entity, base: params.base, quote: params.quote, direction: params.direction });
    return request<TradeMedian>(`/api/trades/median?${qs}`);
  },

  // Manager's Cheque — 3-stage lifecycle (book → pay seller → deliver)
  bookManagerCheque: (body: {
    entity: 'dubaiotc' | 'indiaotc';
    customer_slug: string;
    customer_display_name?: string;
    profile_kind?: 'tracked' | 'treasury_only';
    mc_face_amount: number;
    mc_seller_slug: string;
    mc_seller_display_name?: string;
    mc_seller_role?: 'merchant' | 'other';
    customer_treasury_slug: string;
    customer_pay_asset: string;
    customer_deposit_amount: number;
    /** The share actually paid, derived — lib/mc-deposit.ts. The route re-derives it from mc_deposit_aed. */
    mc_deposit_pct?: number;
    /** The deposit in AED, and AED per unit of the pay asset (omitted on an AED rail). */
    mc_deposit_aed?: number;
    mc_deposit_rate_to_aed?: number;
    mc_customer_fee_bps?: number;
    mc_seller_fee_bps?: number;
    notes?: string;
    deal_id?: string;
    /** Book it although its deposit's chain transfer is already in the books (409 repeat_suspected) — on purpose
     *  (bff routes/manager-cheque.ts BookMCBody, 9d9756c8). */
    confirm_repeat?: boolean;
  }) => request<{
    ok: boolean;
    stage: 'booked';
    deal_id: string;
    face_amount: number;
    deposit_amount: number;
    deduped?: boolean;
  }>('/api/trades/mc', { method: 'POST', body: JSON.stringify(body) }),

  payMcSeller: (dealId: string, body: {
    desk_treasury_slug: string;
    desk_pay_asset: string;
    desk_pay_amount: number;
    rate_to_aed?: number;   // non-AED legs: agreed conversion for the AED meter
    partial?: boolean;      // post a leg, stay pending
    complete?: boolean;     // mark seller fully paid
    leg_index?: number;     // idempotency per leg — the older routes; leg_key wins where the route knows it
    /** ONE PRESS, ONE KEY (26 Oct 2026): minted when the operator presses, kept until the books answer, sent again unchanged, figures and all
     *  (lib/chequePress.ts). 16–64 of A–Z a–z 0–9 _ -. */
    leg_key?: string;
  }) => request<{ ok: boolean; stage: 'seller_paid' | 'partial'; paid_aed_equiv?: number; deal_id: string; deduped?: boolean }>(
    `/api/trades/mc/${encodeURIComponent(dealId)}/pay-seller`,
    { method: 'POST', body: JSON.stringify(body) },
  ),

  deliverMc: (dealId: string, body: {
    customer_balance_amount: number;   // 0 = early-paid cheque, nothing owed at handover
    customer_treasury_slug?: string;
    customer_pay_asset?: string;
    customer_fee_bps?: number;         // fee renegotiated at delivery
    rate_to_aed?: number;              // required when the deposit or the balance is coin
    /** The customer really is paying the balance again on top of what is on
     *  account (the deliver route refuses otherwise, 14 Sept 2026). */
    confirm_money_moved_again?: boolean;
  }) => request<{
    ok: boolean; stage: 'settled'; deal_id: string;
    face_amount: number; balance_amount: number; spread: number;
  }>(`/api/trades/mc/${encodeURIComponent(dealId)}/deliver`,
    { method: 'POST', body: JSON.stringify(body) },
  ),

  /** The cheque, with the tick at which THIS read started (`readTick`, lib/readClock) — the cheque's stages draw their forms only from a read that started after the press they waited for ended. */
  getMcDeal: (dealId: string) => stamped(() =>
    request<{ deal: {
      /** Seller payments the ledger holds that the cheque's meter never counted (mc_pending_seller only; [] otherwise), each with what it takes to send it
       *  again. Absent on a Worker before 26 Oct. */
      mc_uncounted_legs?: McUncountedLeg[];
      /** Every seller payment the meter has COUNTED, at any stage (read from the meter rows): a press sent under a key has `leg_key` and a null index, an older screen's press the index and a null
       *  key, a whole-payment press neither. A kept press found here is settled. Absent on a Worker before it was added. */
      mc_counted_legs?: McCountedLeg[];
      /** What a started delivery put in the ledger, to be finished with the same figures (mc_seller_paid only; null when nothing is). Absent on a Worker before 26 Oct. */
      mc_delivery_started?: McDeliveryStarted | null;
      deal_id: string;
      lifecycle: string;
      customer_slug: string;
      customer_name: string;
      mc_seller_slug: string;
      mc_seller_display_name?: string;
      mc_face_amount: string | number;
      mc_customer_fee_bps: number;
      mc_seller_fee_bps: number;
      mc_deposit_pct: string | number;
      mc_deposit_amount: string | number;
      mc_customer_treasury_slug: string | null;
      mc_seller_pay_treasury_slug: string | null;
      mc_seller_pay_asset: string | null;
      mc_seller_pay_amount: string | number | null;
      mc_seller_paid_at: string | null;
      mc_balance_amount: string | number | null;
      mc_delivered_at: string | null;
      asset_take: string;
      booked_at: string;
      booked_by_admin: string;
      spread_amount: string | number | null;
      notes: string | null;
      void_reason: string | null;
      voided_at: string | null;
    } }>(`/api/trades/mc/${encodeURIComponent(dealId)}`)),

  /* ── Settle later ─────────────────────────────────────────────────────────
   *  Every party with a settle-later trade still open, its box beside its
   *  deals. Reading it also runs the sweep: a party whose box is square in
   *  every asset its open trades touch has nothing outstanding, and its deals
   *  close themselves. */
  settlements: (entity = 'dubaiotc') =>
    request<SettlementsResponse>(`/api/settlements?entity=${entity}`, { changes: true }),
  /** 409 box_not_square unless `force` — the button records a settlement, it
   *  is not one (operator, 2 Sept). */
  settleDeal: (dealId: string, body: { note?: string; force?: boolean } = {}) =>
    request<{ ok: true; deal_id: string; lifecycle: string }>(
      `/api/settlements/${encodeURIComponent(dealId)}/settle`, { method: 'POST', body: JSON.stringify(body) }),
  /** "Still to pay / still to receive": the leg is not settled by the arithmetic;
   *  only money after this moment counts for it. */
  holdLeg: (dealId: string, legs: Array<'take' | 'give'>) =>
    request<{ ok: true; deal_id: string; held: string[] }>(
      `/api/settlements/${encodeURIComponent(dealId)}/hold`, { method: 'POST', body: JSON.stringify({ legs }) }),
  reopenDeal: (dealId: string) =>
    request<{ ok: true; deal_id: string; lifecycle: string }>(
      `/api/settlements/${encodeURIComponent(dealId)}/reopen`, { method: 'POST', body: '{}' }),

  chainActivity: (status = 'pending', limit = 100) =>
    request<ChainResponse>(`/api/chain?status=${status}&limit=${limit}`),

  /** Pull the chains now, then match. Returns what it saw and what it matched. */
  chainSync: () =>
    request<{ ok: true; seen: number; matched: number; unreachable: string[]; unsupported: string[] }>(
      '/api/chain/sync', { method: 'POST' }),

  /** The books entries a pending transfer could be — same wallet, direction
   *  and asset, nearest amount first — so the operator ties by what they can
   *  recognise (party, amount, time) instead of a tx id no screen shows. */
  chainCandidates: (id: number) =>
    request<{ id: number; candidates: ChainCandidate[];
      /** The party the Books' own ties give this transfer's address, when they give ONE (shared or untrusted: null) — the party
       *  Book as trade and Money in open with. `why` is words ("their address · 11 earlier entries"). Mechanisms, 30 Oct 2026. */
      suggested_party?: { slug: string; display_name: string; why: string } | null }>(`/api/chain/${id}/candidates`),

  /** The screening worker's picture (health, spend, watched desk wallets,
   *  flags, onboarding funnel) plus the Books' own wallet register, each row
   *  marked whether the screener watches it. Read-only by contract. */
  kytOverview: () => request<KytOverview>('/api/kyt/overview'),

  /** Every incoming payment to this month's desk wallets as the screener
   *  judged it, the funds by level for each wallet, and the answer before a
   *  deposit to an exchange — over the last 24, 72 or 168 hours. Free: it
   *  reads what the checks stored. */
  kytIncoming: (q: { hours?: 24 | 72 | 168; from?: string; to?: string } | 24 | 72 | 168 = 24) => {
    // A range of Dubai days (the period picker, 28 Oct 2026) or the last hours; a plain number is the old call.
    const o = typeof q === 'number' ? { hours: q } : q;
    const qs = o.from ? `from=${encodeURIComponent(o.from)}${o.to ? `&to=${encodeURIComponent(o.to)}` : ''}` : `hours=${o.hours ?? 24}`;
    return request<KytIncoming>(`/api/kyt/incoming?${qs}`);
  },

  /** Live 7-day in/out for one wallet with Arkham attribution. Costs one
   *  Arkham credit unless the BFF's ten-minute cache answers — so it sits
   *  behind a button, never a page load. A GET that SPENDS is a change: with a
   *  read bound the button would come back at 45 s while the first lookup is
   *  still running upstream, and a second press would buy it again. */
  kytTransfers: (addr: string) =>
    request<KytTransfers>(`/api/kyt/transfers?addr=${encodeURIComponent(addr)}`, { changes: true }),

  /** The energy desk's whole picture: the prepaid balance and where to top it
   *  up, today's spend, what a rental costs now, the treasury wallets and
   *  their energy, the rentals, and what needs attention. A separate service
   *  on the desk Mac — the Books show it and decide nothing about it. */
  energyStatus: () => request<EnergyStatus>('/api/energy/status'),

  /** What a top-up would cost and whether the desk would place it. Orders
   *  nothing — so it is asked before the button that does. */
  energyPreview: (body: { walletId: number; energy: string; durationSec?: number }) =>
    request<EnergyPreview>('/api/energy/preview', { method: 'POST', reads: true, body: JSON.stringify({ ...body, purpose: 'top_up' }) }),

  /** Place a rental. `requestKey` makes pressing twice order once: the screen
   *  mints it WITH the price it shows and sends the same key again on a
   *  retry. `maxCostSun` is that price — the desk will not place a top-up for
   *  more than the operator was shown. A standing rental takes its size from
   *  the wallet's allocation and sends neither. `address` is the wallet's, as
   *  listed: the one the Books' register is asked about (the desk holds the
   *  order to it). */
  energyRent: (body: { requestKey: string; walletId: number; address: string } & ({ purpose: 'top_up'; energy: string; durationSec?: number; maxCostSun: string } | { purpose: 'standing' })) =>
    request<EnergyRentAnswer>('/api/energy/rent', { method: 'POST', body: JSON.stringify(body) }),

  /** List a treasury wallet: one of the Books' register, picked by its id —
   *  the address the desk lists is the register's (operator, 4 Oct 2026, D6:
   *  "books register"). Energy is delegated TO a wallet without anything from it. */
  energyAddWallet: (body: { booksWalletId: number; label: string; standingEnergy?: string }) =>
    request<EnergyWalletAnswer>('/api/energy/wallets', { method: 'POST', body: JSON.stringify(body) }),

  energyUpdateWallet: (id: number, body: { label?: string; standingEnergy?: string; status?: EnergyWallet['status'] }) =>
    request<EnergyWalletAnswer>(`/api/energy/wallets/${id}`, { method: 'POST', body: JSON.stringify(body) }),

  /** Settle by hand an order whose outcome the desk could not learn — after
   *  checking the provider's own order list. `charged` is what it showed. */
  energySettle: (id: number, body: { charged: boolean; note?: string }) =>
    request<EnergyRentAnswer>(`/api/energy/rentals/${id}/settle`, { method: 'POST', body: JSON.stringify(body) }),

  /** One live books transaction by id, for the Chain screen's typed-id match
   *  (14 Sept 2026): what it moved on which wallet, so Match is enabled only
   *  for a real leg. */
  chainTxn: (txn: number) => request<{ txn: number; timestamp: string | null; reference: string | null; kind: string | null; wallet_slug: string | null; direction: 'in' | 'out' | null; asset: string | null; amount: number | null; party: string | null; deal_id: string | null }>(`/api/chain/txn/${txn}`),
  /** ONE TAP FOR A VENUE MOVE (Mechanisms' proposal, 30 Oct 2026): books the move the server pairs with this send — rails, coin,
   *  the chain's exact amount, the venue fee, the chain's time and the hash in the note all derived from its own pairing — and
   *  ties the row, in one transaction. 409 refuses in words (already_tied, no_pair, pair_changed, venue_pending — the last
   *  books anyway with confirm_pending); a retry after a lost answer finds its own posting by reference. tied:false comes with a
   *  `reason`: the posting stands and the next chain read ties it by its note. */
  chainBookMove: (id: number, body: { venue_movement_id: number; confirm_pending?: boolean }) =>
    request<{ ok: true; txn: number; ref: string; tied: boolean; reason?: string }>(`/api/chain/${id}/book-move`,
      { method: 'POST', body: JSON.stringify(body) }),
  chainMark: (id: number, body: {
    action: 'match' | 'ignore' | 'reopen'; ledger_txn_id?: number; deal_id?: string; note?: string;
    /** SEVERAL POSTINGS FOR ONE SEND (db/0061, 30 Sept 2026): each names a books
     *  tx; a part left out is filled by the route with what that posting still
     *  lacks. Replaces ledger_txn_id when present. */
    ties?: Array<{ ledger_txn_id: number; amount?: number | null }>;
  }) =>
    request<{ ok: true; id: number; status: string }>(`/api/chain/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) }),

  voidMc: (dealId: string, reason: string) =>
    request<{ ok: boolean; deal_id: string; prior_lifecycle: string }>(
      `/api/trades/mc/${encodeURIComponent(dealId)}/void`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  /** Void an ordinary (non-MC) trade: reverses every ledger leg, restores the
   *  inventory book from its snapshot, marks the deal voided. The route refuses
   *  unless the deal is still the last event on its book — void newest first. */
  voidTrade: (dealId: string, reason: string) =>
    request<{ ok: boolean; deal_id: string; books_restored?: unknown[] }>(
      `/api/trades/void/${encodeURIComponent(dealId)}`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  /** Void a trade that is NOT the newest on its cost book: the books are
   *  replayed without it and later sales re-metered (POST /api/inventory/
   *  rebuild-v3). dry_run false applies. 11 Sept 2026: Loki's duplicate sale
   *  sat under two newer trades and the plain void refused it. */
  rebuildV3: (body: { void_deal_ids: string[]; void_reason: string; dry_run: boolean; entity?: 'dubaiotc' | 'indiaotc' }) =>
    request<{ dry_run: boolean; unwound?: unknown; plan?: unknown[]; voided?: string[] }>('/api/inventory/rebuild-v3', { method: 'POST', body: JSON.stringify(body) }),

  pendingMcs: (entity = 'dubaiotc') =>
    request<{ entity: string; pending: Array<{
      deal_id: string;
      lifecycle: 'mc_pending_seller' | 'mc_seller_paid';
      mc_seller_slug: string;
      mc_face_amount: number;
      mc_customer_fee_bps: number;
      mc_seller_fee_bps: number;
      mc_deposit_pct: number;
      mc_deposit_amount: number;
      mc_seller_pay_treasury_slug: string | null;
      mc_seller_pay_asset: string | null;
      mc_seller_pay_amount: number | null;
      customer_pay_asset: string;
      booked_at: string;
      mc_seller_paid_at: string | null;
      customer_slug: string;
      customer_name: string;
    }> }>(`/api/trades/mc/pending?entity=${entity}`),

  recentManagerCheques: (entity = 'dubaiotc', limit = 20) =>
    request<{ entity: string; deals: Array<{
      deal_id: string;
      lifecycle: string;
      mc_seller_slug: string;
      mc_face_amount: number;
      mc_customer_fee_bps: number;
      mc_seller_fee_bps: number;
      spread_amount: number;
      booked_at: string;
      mc_seller_paid_at: string | null;
      mc_delivered_at: string | null;
      booked_by_admin: string;
      customer_slug: string;
      customer_name: string;
    }> }>(`/api/trades/mc/recent?entity=${entity}&limit=${limit}`),

  // chainSwap (POST /api/treasury/chain-swap) was removed on 14 Sept 2026 with
  // screens/ChainSwap.tsx (SWEEP-SCOPE §5.11, item 51): the instrument was
  // designed out of the fresh books — the chain lives in the wallet name and
  // a swap is two ordinary moves — and the BFF answers chain_swap_retired.
  // The old route /treasury/chain-swap opens Move money.

  addPartyWallet: (slug: string, body: { chain: string; address: string; label?: string; retire_previous_on_chain?: boolean }, entity = 'dubaiotc') =>
    request<{ wallet: { id: number; chain: string; address: string; label: string | null; status: string; first_seen_at: string }; retired_previous_count: number }>(
      `/api/parties/${encodeURIComponent(slug)}/wallets?entity=${entity}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** THE WALLET REGISTER (routes/wallets.ts, operator 6 Oct 2026): every
   *  address the books know, and the two one-way steps out of use. No delete,
   *  nothing comes back; an address is added through addPartyWallet above. */
  walletRegister: () => request<{ wallets: RegisterWallet[] }>('/api/wallets'),
  changeWallet: (id: string, body: { action: 'retire' | 'archive' | 'label'; reason?: string; label?: string | null }) =>
    request<{ ok: true; id?: string; label?: string | null;
              wallet?: { id: string; status: string; retired_at: string | null; unwatched_at: string | null };
              /** On an archive: whether the KYT screening worker was told (routes/kyt.ts unwatchInScreener). */
              kyt?: { told: true; removed: boolean } | { told: false; reason: string };
              /** On a retire or an archive: what the energy desk did with it (Energy's feed); absent when the
               *  Books are not wired to a desk. */
              energy?: { told: true; to: 'retired' | 'paused' } | { told: true; nothing: true } | { told: false; reason: string } }>(
      `/api/wallets/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** A payout address made payable by the Books (N-061): full role, from the desk's own connection only.
   *  `already` when it was approved before — nothing changed. */
  approvePayoutAddress: (id: string) =>
    request<{ ok: true; already?: true;
              wallet: { id: string; chain: string; address: string; payout_approved_at: string; payout_approved_by: string; payout_approved_via: string } }>(
      // Behind Cloudflare Access (P-035): its session cookie for the API goes with this one call.
      `/api/wallets/${encodeURIComponent(id)}/approve-payout`, { method: 'POST', credentials: 'include' }),

  // Live workflow routes (§6)
  // A repeated deal_id / reference answers 200 {ok, deduped:true} with the
  // request's effective_at IGNORED — the books keep the first posting's date.
  // The type has to say so, or a screen reports the retry's date as booked
  // (review, 5 Sept 2026). `warnings` are the inventory book's asides.
  // `converted: true` comes back on a settle-on-account booking (see
  // BookTradeBody.settle_on_account) — both legs were netted, nothing awaits.
  bookTrade:    (body: BookTradeBody) => request<{ ok: boolean; placeholder?: boolean; received?: unknown; deduped?: boolean; deal_id?: string; warnings?: string[]; converted?: boolean }>('/api/trades', { method: 'POST', body: JSON.stringify(body) }),
  // Between parties: one party's balance moves to another's, no desk account
  // touched (11 Sept 2026, "P2P, like Loki"). See P2pBody.
  p2p:          (body: P2pBody) => request<P2pResponse>('/api/p2p', { method: 'POST', body: JSON.stringify(body) }),
  transfer:     (body: TransferBody) => request<{ ok: boolean; transaction?: unknown; deduped?: boolean; reference?: string }>('/api/transfers', { method: 'POST', body: JSON.stringify(body) }),
  receipt:      (body: { destination: string; amount: Monetary; reference: string; payment_method?: string; payment_method_detail?: string; notes?: string }) =>
                  request<{ ok: boolean; transaction?: unknown; deduped?: boolean }>('/api/receipts', { method: 'POST', body: JSON.stringify(body) }),
  payment:      (body: { source: string; gross: Monetary; network_fee?: Monetary; fee_account?: string; reference: string; payment_method?: string; payment_method_detail?: string; notes?: string }) =>
                  request<{ ok: boolean; transaction?: unknown; deduped?: boolean }>('/api/payments', { method: 'POST', body: JSON.stringify(body) }),
  // Expense + owner equity. An expense reduces profit, a drawing does not —
  // separate endpoints so the two can never land on the same account.
  recordExpense: (body: ExpenseBody) =>
                  request<{ ok: boolean; transaction?: unknown; deduped?: boolean }>('/api/expenses', { method: 'POST', body: JSON.stringify(body) }),
  recordCapital: (body: EquityMoveBody) =>
                  request<{ ok: boolean; transaction?: unknown; deduped?: boolean }>('/api/equity/capital', { method: 'POST', body: JSON.stringify(body) }),
  recordDrawing: (body: EquityMoveBody) =>
                  request<{ ok: boolean; transaction?: unknown; deduped?: boolean }>('/api/equity/drawing', { method: 'POST', body: JSON.stringify(body) }),
  // The desk's share of a sub-broker's balance — no cash moves (17 Sept 2026). See ProfitShareBody.
  profitShare:  (body: ProfitShareBody) => request<ProfitShareResponse>('/api/expenses/profit-share', { method: 'POST', body: JSON.stringify(body) }),
  // A delivery charge raised on a party — its own entry, its own profit line (2 Oct 2026). See ChargeBody.
  recordCharge: (body: ChargeBody) => request<ChargeResponse>('/api/charges', { method: 'POST', body: JSON.stringify(body) }),
  adjust:       (body: { source: string; destination: string; amount: Monetary; reason: string; reference: string }) =>
                  request<{ ok: boolean; transaction?: unknown; deduped?: boolean }>('/api/adjustments', { method: 'POST', body: JSON.stringify(body) }),
  summary:      () => request<{
    entity: string;
    today: {
      trade_count: number;
      volume_per_asset: Record<string, number>;
      spread_per_asset: Record<string, number>;
    };
  }>('/api/summary'),

  // Profit and loss. Named periods run period-to-date on Asia/Dubai boundaries;
  // pass from/to (YYYY-MM-DD) with period 'custom' for an explicit window.
  pnl: (params: { entity?: string; period?: PnlPeriodKey; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.entity) qs.set('entity', params.entity);
    if (params.period) qs.set('period', params.period);
    if (params.from)   qs.set('from', params.from);
    if (params.to)     qs.set('to', params.to);
    return request<PnlResponse>(`/api/summary/pnl${qs.toString() ? `?${qs}` : ''}`);
  },

  // Recent expense postings, newest first, with per-asset totals. `truncated`
  // means the scan did not reach `from` — the totals are partial, say so.
  listExpenses: (params: { from?: string; to?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.from)  qs.set('from', params.from);
    if (params.to)    qs.set('to', params.to);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<ExpenseListResponse>(`/api/expenses${qs.toString() ? `?${qs}` : ''}`);
  },

  /**
   * Trading performance — per-deal cost vs price, bucketed over time.
   *
   * Typed like every other method rather than a generic `get`: the client owns
   * the contract, so a route change breaks the build instead of the screen.
   */
  tradingReport: (params: {
    bucket?: 'day' | 'week' | 'month' | 'year' | 'all';
    from?: string; to?: string; party?: string;
    delivery_method?: string; quote?: string;
  } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v));
    return request<TradingReportResponse>(`/api/reports/trading${qs.toString() ? `?${qs}` : ''}`);
  },

  /** Is the legacy mirror alive, and does it need the operator? */
  mirrorHealth: () => request<MirrorHealthResponse>('/api/ingest/health'),

  /** Legacy trades whose counterparty is only free text — the attach-a-name queue. */
  unnamedNames: () => request<UnnamedResponse>('/api/reports/unnamed'),

  attachName: (body: { name_key: string; name_seen?: string; party_slug?: string; ignore?: boolean; note?: string }) =>
    request<{ ok: boolean; repointed: number }>('/api/reports/unnamed/attach', {
      method: 'POST', body: JSON.stringify(body),
    }),

  /** What the desk owns, owes, and is worth — a position at an instant.
   *  Its own mount, not under /api/reports/*: a P&L measures a period. */
  balanceSheet: (entity = 'dubaiotc', asOf?: string) =>
    request<BalanceSheetResponse>(`/api/balance-sheet?entity=${entity}${asOf ? `&as_of=${asOf}` : ''}`),

  /* ── Period close (GET/POST /api/periods) ───────────────────────────────
   * A month is a PERIOD, closed once, after it has ended. The close sweeps
   * the memo-pair P&L into equity:{e}:retained; preview computes the same
   * numbers and posts nothing, so it is safe to call at any time. */
  listPeriods: (entity = 'dubaiotc') =>
    request<PeriodsResponse>(`/api/periods?entity=${entity}`),

  previewPeriod: (period: string, entity = 'dubaiotc') =>
    request<PeriodPreview>(`/api/periods/${period}/preview?entity=${entity}`),

  closePeriod: (period: string, entity = 'dubaiotc') =>
    request<ClosePeriodResponse>(`/api/periods/${period}/close`, {
      method: 'POST', body: JSON.stringify({ entity }),
    }),

  /* ── Migration handover (GET/PATCH /api/migration) ──────────────────────
   * The hand-entry checklist. `drift_after` is written by
   * scripts/migration-reconcile.mjs, not by the BFF — the Worker cannot reach
   * legacy Mongo, so it serves the verdict rather than computing it. */
  migrationList: (status: MigrationStatus | 'all' = 'pending', limit = 100) =>
    request<MigrationResponse>(`/api/migration?status=${status}&limit=${limit}`),

  migrationMark: (legacyId: string, body: { status: MigrationStatus; deal_id?: string; reason?: string }) =>
    request<{ ok: true; legacy_id: string; status: string }>(`/api/migration/${legacyId}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  reopenPeriod: (period: string, reason: string, entity = 'dubaiotc') =>
    request<{ ok: true; period: string; reverted: string[] }>(`/api/periods/${period}/reopen`, {
      method: 'POST', body: JSON.stringify({ entity, reason }),
    }),

  /** Every onboarding lead that needs a person — stuck, waiting for a decision, approved and not
   *  contacted, escalated — as the onboarding worker lists and labels them (routes/leads.ts).
   *  Read-only: every decision stays on the cards in the Telegram ops group. */
  /** Every queue's first page; with `page`, one queue read on from `offset`. */
  onboardingLeads: (page?: { queue: LeadQueue; offset: number }) =>
    request<OnboardingLeads>(page ? `/api/onboarding/leads?queue=${page.queue}&offset=${page.offset}` : '/api/onboarding/leads'),
  /** One lead's whole record, by its session id. */
  onboardingLead: (id: string) => request<LeadDetails>(`/api/onboarding/leads/${encodeURIComponent(id)}`),
  /** Every approved client, a page of 100 from `offset`. */
  onboardingClients: (offset = 0) => request<ApprovedClients>(`/api/onboarding/clients?offset=${offset}`),
  /** The potential leads — still in the chat with the bot, not yet with the team — a page of 100 from `offset`, with
   *  the funnel: chats by state, client records by status, the last 24 hours. */
  onboardingProspects: (offset = 0) => request<OnboardingProspects>(`/api/onboarding/prospects?offset=${offset}`),
  /** The chat button for a person the page cannot link to by itself: the bot sends the signed-in admin a one-tap button
   *  in Telegram, and this answers where to open it. Changes nothing about the person. */
  onboardingChatLink: (ref: { sessionId: string } | { customerId: string }) =>
    request<ChatLink>('/api/onboarding/chat-link', { method: 'POST', body: JSON.stringify(ref) }),
  /** An approved customer becomes a Books party (D3, the operator, 15 Oct 2026: "yes, build J1 as a button"): made
   *  from the record's name, or with `slug` given; `link` names an existing party to link to instead. */
  onboardingMakeParty: (body: { sessionId: string; slug?: string; link?: string }) =>
    request<MadeParty>('/api/onboarding/party', { method: 'POST', body: JSON.stringify(body) }),

};

/* ── Period close (GET /api/periods, /:period/preview) ─────────────────────
 * Canonical definition lives above the routes in bff/src/routes/periods.ts.
 * Amounts are DISPLAY units; the BFF converts from minor at its read boundary.
 * A gate with ok:false blocks the close and names why — the screen must show
 * the detail, never just a red light. */
/** `units`: the figures in `detail` — 'display' (the asset's own units, signed and grouped) or 'minor'; absent from a
 *  server older than 23 Oct 2026 (smallest unit) and from a check with no figures. */
export interface PeriodGate { name: string; ok: boolean; detail: string; units?: 'display' | 'minor' }

export interface PeriodAssetClose {
  asset: string;
  /** P = M + Q — what reaches equity:{e}:retained. */
  profit: string;
  /** M — margin the memo pairs only MEASURED; its value sits in the position. */
  memo_margin: string;
  /** Q — profit that ALREADY moved equity through real postings. */
  real_pnl: string;
  meter_resets: Record<string, string>;
}

export interface PeriodPreview {
  period: string;
  entity: string;
  window: { start: string; end: string };
  period_ended: boolean;
  gates: PeriodGate[];
  assets: PeriodAssetClose[];
}

export interface PeriodRow {
  period: string;
  period_end: string;
  status: 'open' | 'closed';
  close_seq: number;
  closed_at: string | null;
  closed_by: string | null;
}

export interface PeriodLeg {
  period: string;
  asset: string;
  reference: string;
  profit: string;
  memo: string;
  real: string;
  posted_at: string;
}

export interface PeriodsResponse {
  entity: string;
  regime_start: string;
  periods: PeriodRow[];
  legs: PeriodLeg[];
}

export interface ClosePeriodResponse {
  ok: true;
  period: string;
  entity: string;
  closed: Array<{ asset: string; profit: string; memo_margin: string; real_pnl: string }>;
}

/* ── Balance sheet (GET /api/balance-sheet) ────────────────────────────────
 * Numerics arrive as NUMBERS here, unlike the trading report: these are ledger
 * balances converted from integer minor units by the BFF, not pg NUMERIC text.
 *
 * `aed` is null in two DIFFERENT situations and the screen must tell them
 * apart — `posted: false` means the account exists in the chart but has never
 * been posted to (render "—"), while `posted: true` with `aed: null` means a
 * real balance that could not be valued (render "—" AND surface the warning).
 */
export interface BalanceSheetLine {
  key: string;
  label: string;
  /** Major units, signed for presentation, keyed by raw asset code. */
  amounts: Record<string, number>;
  aed: number | null;
  accounts: number;
  posted: boolean;
  caveat?: string;
  /** Assets on this line whose AED came from a STATED price rather than from
   *  the desk's own dealing. Non-empty means part of `aed` is an estimate and
   *  the line must say so — a valued figure is never mixed silently into a
   *  book figure (decision of 4 Sept 2026). */
  stated?: string[];
  /** Only on `equity.position`: what the number is made of, split by what
   *  created each entry. Presentation only — never re-added to any total. */
  parts?: Array<{
    key: string; label: string; amounts: Record<string, number>;
    aed: number | null; txns: number; unpriced?: string[];
  }>;
}

export interface BalanceSheetSection {
  key: string;
  label: string;
  lines: BalanceSheetLine[];
  totals: Record<string, number>;
  total_aed: number | null;
}

export interface BalanceSheetRatePair {
  pair: string;
  median: number;
  trades: number;
  low: number;
  high: number;
  last: string | null;
}

export interface BalanceSheetNote {
  code: string;
  tone: 'warn' | 'info';
  message: string;
}

export interface BalanceSheetResponse {
  entity: string;
  /** The instant the figures stand at: when the live sheet was read, or the last moment of the day asked for. */
  as_of: string;
  /** The Dubai day asked for (`?as_of=YYYY-MM-DD`): the books as at the end of it — every entry dated on or before
   *  it, at that day's rates. Null on the live sheet. */
  as_of_date?: string | null;
  reporting_currency: string;
  rates: {
    basis: string;
    pairs: BalanceSheetRatePair[];
    cross: {
      pair: string;
      inr_per_aed_display: number;
      inr_per_aed_precise: number;
      derivation: string;
    } | null;
    pegged: Array<{ pair: string; rate: number; basis: string }>;
    /** The stated prices THIS read leaned on (db/0035). Empty is a fact; it is
     *  not the same fact as `stated_available: false`, which means the table
     *  could not be read and the sheet must warn rather than report "no rate". */
    stated?: StatedValuation[];
    stated_available?: boolean;
  };
  sections: BalanceSheetSection[];
  net_assets_aed: number | null;
  check: {
    ledger_residual_by_asset: Record<string, number>;
    presentation_gap_aed: number | null;
    /** false also when the sheet could not be CHECKED — unverified is not a pass. */
    closes: boolean;
  };
  notes: BalanceSheetNote[];
  meta: {
    accounts_read: number;
    ledger: string;
    position_account: string;
  };
}

/* ── Trading report (GET /api/reports/trading) ─────────────────────────────
 * Numerics arrive as STRINGS: pg returns NUMERIC as text and parsing them into
 * JS floats here would reintroduce, at the display layer, exactly the precision
 * loss the ledger works in integer minor units to avoid.
 */
export interface TradingSeriesRow {
  period: string | null;          // null on the cumulative bucket
  quote_ccy: string;
  inv_asset: string;
  deals: string;
  volume_qty: string;
  volume_quote: string;
  deals_with_margin: string;
  trading_margin: string | null;  // null = the engine realised none — NOT zero
  /** Non-null only when a BUY realised margin by covering a short (rule R-C).
   *  Ordinary buys realise nothing; this is the one case that does. */
  short_cover_margin: string | null;
  delivery_fees: string | null;
  avg_cost_rate: string | null;
  avg_sell_rate: string | null;
  avg_buy_rate: string | null;
}
export interface TradingMixRow {
  trade_class: string; quote_ccy: string; deals: string;
  volume_quote: string; margin: string | null;
  share_of_volume_pct: number | null; share_of_margin_pct: number | null;
}
export interface TradingPartyRow {
  party_slug: string; party_type: string; quote_ccy: string;
  deals: string; volume_quote: string; margin: string | null;
}
export interface TradingReportResponse {
  bucket: 'day' | 'week' | 'month' | 'year' | 'all';
  series: TradingSeriesRow[];
  mix: TradingMixRow[];
  by_party: TradingPartyRow[];
  note: string;
}

/* ── Mirror health (GET /api/ingest/health) ────────────────────────────────
 * `unknown` is deliberately its own state and is NOT treated as healthy: never
 * having heard from the mirror is not the same as the mirror being fine.
 */
export interface MirrorHealthResponse {
  status: 'ok' | 'attention' | 'halted' | 'stale' | 'unknown';
  headline: string;
  last_round_at: string | null;
  minutes_since: number | null;
  posted: number;
  deduped: number;
  skipped: number;
  pending: number;
  operator_queue: number;
  failed: number;
  voids: number;
  halted: boolean;
  halt_reason: string | null;
  halt_reference: string | null;
  checkpoint_at: string | null;
  newest_event_at: string | null;
  recon_verdict: string | null;
  rounds_24h: number;
  recent: Array<{ round_at: string; posted: number; failed: number; halted: boolean }>;
}

/* ── Attach-a-name queue (GET /api/reports/unnamed) ────────────────────────
 * `suggestion` is null unless the description matches a party EXACTLY or
 * uniquely case-insensitively. Substring matching is deliberately absent: "Ash"
 * would match cash.aed.dxb and "Han" would match thanos.
 */
export interface UnnamedGroup {
  name_key: string;
  name_seen: string;
  deals: string;
  first_seen: string | null;
  last_seen: string | null;
  volume_quote: string | null;
  quote_ccy: string | null;
  resolved: boolean | null;
  ignored: boolean | null;
  note: string | null;
  attached_slug: string | null;
  suggestion: { slug: string; party_type: string } | null;
}
export interface UnnamedResponse {
  entity: string;
  open: number;
  groups: UnnamedGroup[];
  parties: Array<{ id: string; slug: string; display_name: string | null; party_type: string }>;
  note: string;
}


/* ── Migration handover ────────────────────────────────────────────────────
 * Canonical shape lives above the routes in bff/src/routes/migration.ts. */
export type MigrationStatus = 'pending' | 'entered' | 'skipped' | 'blocked';

export interface MigrationDrift {
  at: string;
  checked: number;
  drift: Array<{ slug: string; name: string; asset: string; legacy: number; expected: number; actual: number; drift: number }>;
  unmapped: Array<{ id: string; name: string; asset: string; amount: number }>;
}

export interface MigrationRow {
  legacy_id: string;
  kind: 'txn' | 'pri';
  occurred_at: string;
  party: string | null;
  counterparty: string | null;
  tran_type: string | null;
  tran_from: string | null;
  quantity: string | null;
  rate: string | null;
  total: string | null;
  asset: string | null;
  quote_ccy: string | null;
  description: string | null;
  status: MigrationStatus;
  entered_deal_id: string | null;
  /** Deals whose amounts match this legacy row on either leg, booked within a
   *  fortnight of it and not already claimed by another row. Suggestions the
   *  operator clicks instead of typing a reference by hand. */
  candidates: Array<{
    deal_id: string; direction: string; take: string; give: string; booked_at: string;
  }> | null;
  entered_at: string | null;
  skip_reason: string | null;
  drift_after: MigrationDrift | null;
  raw: Record<string, unknown> | null;
}

export interface MigrationResponse {
  handover: { id: number; kind: string; taken_at: string; mirror_cursor: string | null; notes: string | null } | null;
  /** When scripts/migration-worklist.mjs last pulled from legacy. The rows are
   *  only as fresh as this — the console cannot pull them itself. */
  worklist_refreshed_at: string | null;
  progress: { total: number; pending: number; entered: number; skipped: number; blocked: number };
  rows: MigrationRow[];
}

/* ── Settle later (GET /api/settlements) ─────────────────────────────────── */
/** How one leg of a settle-later trade closed.
 *
 *  'on account' is a leg of a MARKED party's trade (db/0036, the running
 *  account) on which money moved and the remainder rode onto their balance;
 *  `rode` says how much. The value is spelt with a space on purpose —
 *  legWords() prints `${asset} ${how}`, so it reads "INR on account" with no
 *  lookup table and no second vocabulary to keep in step.
 *
 *  `rode` is money the desk is still owed. It is on the leg, in the deal's
 *  note and in the settlement's audit row, and no screen may quietly drop it. */
export interface LegState {
  asset: string; amount: number; settled: boolean;
  how: 'received' | 'paid' | 'netted' | 'on account' | 'open';
  open: number;
  rode?: number;
  /** The money postings the sweep DREW ON to settle this leg — each one a
   *  receipt or payment already in the books. 5 Sept 2026: with a back-dated
   *  trade (Loki, 3 Sept) these are the 3 Sept money legs, and naming them
   *  is how a reader proves the trade was settled from that money and not
   *  from a later, unrelated movement. Optional: an older payload reads as
   *  "not stated", never as a type error. */
  drew?: Array<{ tx_id: number; asset: string; amount: number }>;
}
export interface SettlementParty {
  slug: string;
  display_name: string | null;
  party_type: string;
  box: Record<string, number>;     // positive = owes you · negative = you hold theirs (folded assets)
  open_assets: string[];
  oldest: string;
  deals: Array<DealRow & { legs: { take: LegState; give: LegState } | null }>;
}
export interface SettlementsResponse {
  entity: string;
  parties: SettlementParty[];
  /** Trades the sweep closed on THIS read. They are gone from `parties` above,
   *  so this is the only chance the reader gets to be told — including `rode`,
   *  what stayed on the party's running account instead of being paid. A
   *  figure that leaves a screen without being written down is how a desk
   *  loses track of what it owes. */
  swept: Array<{ deal_id: string; party_slug: string; rode: Array<{ asset: string; amount: number }> }>;
  totals: { deals: number; parties: number };
}

/* ── On-chain activity (GET /api/chain, PATCH /api/chain/:id) ──────────────
 * The wallet's own history, matched against the books. `status` is derived
 * server-side from whether a match row exists — never stored, never sent up. */
export interface ChainRow {
  id: number;
  chain: string;
  tx_hash: string;
  wallet_slug: string;
  direction: 'in' | 'out';
  counterparty_address: string | null;
  asset: string;
  amount: number;
  block_time: string;
  status: 'pending' | 'auto' | 'manual' | 'ignored';
  confidence: 'exact' | 'rounded' | 'grouped' | 'group-sum' | 'unique-amount' | 'remainder' | null;   // db/0038
  delta: number | null;
  ledger_txn_id: number | null;
  /** A SEND TIED TO SEVERAL POSTINGS (db/0061, 30 Sept 2026): the postings in
   *  the order the ties were written and the part of the send each takes;
   *  ledger_txn_id is null then, because "which posting" has no one answer.
   *  Absent on an older Worker; tie_n is 0 or 1 on every pre-0061 row. */
  tie_n?: number;
  tie_txns?: number[];
  tie_amounts?: Array<number | null>;
  /** THE SETS A PENDING SEND WOULD COVER TOGETHER (rule b), as the sync last
   *  computed them — the same postings its note names in prose. One tap ties
   *  the send to all of them, each taking what it still lacks. */
  covers?: Array<{ txns: number[]; refs: Array<string | null>; parties: Array<string | null>; total: number | string }> | null;
  /** What the tied posting moved on this wallet, the chain's sum across every
   *  send tied to it, and the leg's kind — so the screen can say "chain sent
   *  24,703.55, trade says 24,678.85" (sweep item 26, 14 Sept 2026). */
  leg_amount?: number | null;
  chain_sum?: number | null;
  leg_kind?: string | null;
  /** MORE TIED THAN THE ENTRY HOLDS (Mechanisms 40a503e1, 4 Nov 2026): the sends tied to this row's entry, on its leg,
   *  carry `over_by` above what the entry moved, beyond dust — and the server's words for it ("Tied rows exceed this entry
   *  by 100 USDC"). Information, never a block; null when they do not, absent on an older Worker. */
  over_by?: number | null;
  over_tied?: string | null;
  deal_id: string | null;
  note: string | null;
  /** THE EXCHANGE MOVEMENT THIS ROW IS, when the venue's own record meets it
   *  (16 Sept 2026, bff/src/routes/chain.ts venueLinkSql: same hash, or a
   *  deposit's address + exact amount, or a withdrawal's amount ± the venue's
   *  fee within 6 h). An 'out' row is a deposit onto the venue, an 'in' row a
   *  withdrawal from it; the Chain screen opens Move money filled in from
   *  these. `venue_slug` is the venue account in the rail grammar
   *  (`exchange.bitget`, `-india` for the India desk) — a URL parameter,
   *  never a word on screen. `venue_fee` is the venue's fee as a positive
   *  figure in `venue_fee_asset`. Absent or null on an older Worker and when
   *  the row meets no movement. */
  venue?: string | null;
  venue_slug?: string | null;
  venue_kind?: 'deposit' | 'withdrawal' | null;
  venue_movement_id?: number | null;
  /** The venue account's Books name ("Main" → "Bitget Main") and the venue's own status of the movement ("success",
   *  "pending", …) — Mechanisms, 30 Oct 2026; absent on an older Worker. */
  venue_label?: string | null;
  venue_status?: string | null;
  venue_fee?: number | null;
  venue_fee_asset?: string | null;
  /** THE WALLET'S OWN ROW (Mechanisms, 2 Nov 2026): a custody sweep the wallet books itself (`sweep`), or a pot's send
   *  into one of its deposit addresses (`desk_send`) — so the screen offers no Book for it. `state` is where the
   *  wallet's booking stands (waiting_tag, waiting, not_queued, booked, settled, refused, not_swept, swept) and
   *  `words` says it. Null for every other row; absent on an older Worker. */
  custody?: { kind: 'sweep' | 'desk_send'; reference: string | null; state: string; words: string } | null;
}
export interface ChainResponse {
  status: string;
  counts: Record<string, { n: number; net: number }>;
  chain_synced_at: string | null;       // last time the chains were READ
  newest_transfer_at: string | null;    // last time a NEW transfer was seen
  rows: ChainRow[];
}
/** One books entry a pending transfer could be (GET /api/chain/:id/candidates). */
export interface ChainCandidate {
  txn: number;                 // the books tx id the PATCH needs
  asset: string;
  amount: number;
  timestamp: string;
  reference: string | null;    // DEAL-… / PAY-… / RCPT-…
  kind: string | null;         // metadata.type: trade · transfer · mc_pay_seller …
  note: string | null;
  delta: number;               // (books remaining) − chain
  tied: number;                // transfers already tied to this entry
  tied_sum: number;            // their total
  remaining: number;           // amount − tied_sum: what the booking still lacks
  party_slug: string | null;
  party_name: string | null;
}

/** EVERY MONEY VIEW REFRESHES THE MOMENT A POSTING SUCCEEDS (operator, 2 Sept:
 *  "why this lag? make it swift & immediate"). The ledger answers instantly —
 *  only the client cache was serving 30-second-old balances. One helper, called
 *  from every posting's onSuccess on every screen, so no view ever shows money
 *  where it no longer is. Fire-and-forget; refetches dedupe internally. */
import type { QueryClient } from '@tanstack/react-query';
export function refreshMoneyViews(qc: QueryClient): void {
  // EVERY view that reads money, including the reports. Profit, Balance sheet,
  // Trading, Inventory and Close were missing, so after a posting they served
  // pre-posting numbers for up to 30 seconds — the operator could read a profit
  // figure that did not include the trade just booked (3 Sept audit).
  for (const key of [['party'], ['parties'], ['treasury'], ['summary'], ['mc'], ['chain'],
                     ['deals'], ['flows'], ['settlements'], ['trades'],
                     // The movement ledger and a party's statement read the
                     // same money as everything above; a posting that does not
                     // reach them leaves the operator verifying against a
                     // history that is missing the entry he just made.
                     ['movements'], ['statement'], ['cex-fills'],
                     ['pnl'], ['balance-sheet'], ['trading-report'], ['inventory'],
                     ['periods'], ['period-preview']]) {
    qc.invalidateQueries({ queryKey: key });
  }
}

// ── KYT (GET /api/kyt/*) — bff/src/routes/kyt.ts proxies the onboarding
// worker's read-only admin API; shapes per onboarding-agent/docs/kyt-api-contract.md.
export type KytBand = 'low' | 'medium' | 'high' | 'severe';

// The screener's own account of itself (worker, 1 Oct 2026). `lastOk` is the
// last time a provider REALLY answered — never the last time a job ran.
export type KytProviderState = 'ok' | 'dark' | 'unknown' | 'not_enabled';
export interface KytProvider {
  enabled: boolean;
  state: KytProviderState;
  lastOk: string | null;
  lastFail: string | null;
  detail: string | null;
}
export type KytProviderKey = 'gl' | 'arkham' | 'ofac' | 'oracle' | 'explorer';
/** Last night's check of the watched wallets. `state` is absent on a record
 *  written by the worker before 1 Oct 2026. */
export interface KytSweep {
  state?: 'ok' | 'partial' | 'dark' | 'budget' | 'idle' | 'not_enabled';
  targets: number;
  attempted?: number;
  checked: number;
  noBand?: number;
  cached?: number;
  errors?: number;
  alerts: number;
  budget: number;
  riskScoring?: boolean;
  /** The night stopped because the day's Arkham credits ran out. */
  stoppedOnBudget?: boolean;
  lastError?: string;
  at: string | null;
}
/** The inflow ring (senders paying a watched wallet). `disabled` unless the
 *  operator turns it on — on pay-as-you-go it is the costly one. `screened`
 *  counts the senders that got an answer, of the `senders` found. */
export interface KytCounterparties {
  state?: 'ok' | 'partial' | 'dark' | 'budget' | 'idle' | 'not_enabled' | 'disabled';
  wallets: number;
  inflowsRead?: number;
  /** Wallets whose inflow page came back full — older transfers lie behind it, unread. */
  fullPages?: number;
  /** Absent from a worker that predates the counters: its `ok` cannot be verified. */
  senders?: number;
  screened: number;
  /** Senders answered with a name and no risk band — a shortfall only when `riskScoring` is true. */
  noBand?: number;
  riskScoring?: boolean;
  errors?: number;
  flagged: number;
  /** The ring stopped because the day's Arkham credits ran out. */
  stoppedOnBudget?: boolean;
  at: string | null;
}

export interface KytWatchedWallet {
  chain: string;
  address: string;          // FULL — mask on screen, link to arkm.com
  label?: string;
  band: KytBand | null;
  entity?: string;
  lastChecked: string | null;
}
export interface KytFlag {
  created_at: string;
  chain: string;
  address: string;
  provider: string;
  risk_band: KytBand | null;
  risk_score: number | null;
}
export interface KytBooksWallet {
  chain: string;
  address: string;
  label: string | null;
  status: 'active' | 'flagged' | 'archived';
  /** Set when `status` is 'archived': a desk address no longer handed out — and
   *  still on this list, because the Books still read it. */
  retired_at?: string | null;
  risk_score: number | null;
  risk_checked_at: string | null;
  last_seen_at: string | null;
  slug: string;
  display_name: string;
  party_type: 'own_account' | 'customer' | 'counterparty';
  treasury_slug: string | null;
  entity: string;
  watched: boolean;
  /** Not in the desk list, but the freeze watch holds it on an issuer's network (3 Nov 2026): frozen coins watched, payments
   *  not read here, no GL score. Absent from an older server. */
  freeze_watched?: boolean;
  swept_at?: string | null;
  band: KytBand | null;
  entity_name?: string | null;
  last_checked: string | null;
}
export interface KytOverview {
  fetched_at: string;
  spend: {
    arkhamToday: number; arkhamBudget: number; gl: number; glBudget?: number;
    // 1 Oct 2026 — Arkham in credits with a balance; the GL pool the operator set.
    glRemaining?: number; glActiveTo?: string | null; glPoolSource?: 'ops' | 'legacy';
    arkhamRemaining?: number | null; arkhamPoolSource?: 'ops' | 'unknown'; arkhamRings?: string[]; arkhamRisk?: boolean;
  };
  meta: { ofac?: string; alarm: string | null; sweep: string | null; cpSweep: string | null; rates: string };
  wallets: KytWatchedWallet[];
  flags: KytFlag[];
  books_wallets: KytBooksWallet[] | null;
  books_error: string | null;
  // 1 Oct 2026, all optional: a BFF deployed before the screen sends none.
  providers?: Partial<Record<KytProviderKey, KytProvider>>;
  sweep?: KytSweep | null;
  counterparties?: KytCounterparties | null;
  /** Treasury wallets the Books register against those the screener watches;
   *  null when the register could not be read (unknown, not zero). */
  coverage?: { treasury: number; watched: number; retired?: number } | null;
  /** Addresses the Books stopped watching that the screening worker still screens — a missed push, or a /watch by hand. */
  unwatched_screened?: Array<{ chain: string; address: string; label: string | null }>;
  /** What a Trace can cost right now, or why it cannot run. `unsure`: Arkham's
   *  last call failed — the trace may fail too, but it may be asked. */
  trace?: { rows: number; credits: number; off: 'arkham_off' | 'budget' | null; unsure?: boolean };
}
export interface KytTransferRow {
  ts: string;
  chain: string;
  dir: 'in' | 'out';
  usd: number;
  token: string;
  amount: number;
  hash: string;
  counterparty: { address: string; entity?: string; label?: string };
}
export interface KytTransfers {
  status: 'ok' | 'error' | 'not_enabled' | 'budget_exhausted';
  rows: KytTransferRow[];
  fetched_at?: string;
  cached: boolean;
}

// ── Energy (/api/energy/*) — bff/src/routes/energy.ts proxies the energy desk
// (energy/ in this repository, a separate service). The shapes are the desk's
// own (energy/src/desk.mjs status()). AMOUNTS ARE STRINGS OF DIGITS — sun for
// TRX, whole units for energy — so no figure crosses the wire as a float;
// instants are ISO strings.
export type EnergyAlarmLevel = 'ok' | 'warn' | 'critical';
export type EnergyRentalState = 'requested' | 'ordered' | 'delegated' | 'expired' | 'refused' | 'unconfirmed' | 'closed';
export type EnergyPurpose = 'top_up' | 'standing';

export interface EnergyAlarm {
  key: string; level: EnergyAlarmLevel; text: string;
  /** What the alarm is ABOUT, as the desk says it (energy/src/alarm.mjs puts both on every alarm — 22 Oct 2026): identity, not read out of the
   *  sentence. Absent from a desk older than that; the screen treats them as data from outside (lib/energyOrder.ts alarmAbout, alarmRentalIds). */
  rentalIds?: number[]; walletIds?: number[];
}

export interface EnergyRental {
  id: number;
  /** The key the order was placed under — how the screen tells its own order's rental from another's. */
  requestKey: string;
  walletId: number;
  receiver: string;
  purpose: EnergyPurpose;
  requestedBy: string;
  energy: string;
  neededEnergy: string;
  durationSec: number;
  costSun: string;             // the quoted total — the most it may cost
  burnCostSun: string;
  savingBps: number;
  state: EnergyRentalState;
  chargedSun: string | null;   // the provider's own figure, once it gives one
  detail: string | null;
  createdAt: string;
  delegatedAt: string | null;
  expiresAt: string | null;
  chainSeenAt: string | null;  // when the energy was seen on the wallet itself
  /** The desk cannot settle this one itself; a person may, after checking the provider. */
  settleable: boolean;
}

export interface EnergyWallet {
  id: number;
  address: string;
  label: string;
  status: 'active' | 'paused' | 'retired';
  standingEnergy: string;      // "0" = no standing allocation
  standingCovered: string;
  standingNote: string | null; // why the allocation is not in place, when it is not
  activated: boolean | null;   // null = not looked at yet
  trxBalanceSun: string | null;
  energyLimit: string | null;
  available: string | null;
  observedAt: string | null;
  observeError: string | null;
  observeErrorAt: string | null;
  rentedEnergy: string;
  nextExpiry: string | null;
  unresolved: EnergyRental | null;
  /** When the desk first saw the account on the chain — kept for good (desk db/005). Absent from a desk before it. */
  activatedAt?: string | null;
  /** The wallet's newest activation, if the desk lists one (desk db/005). */
  activation?: EnergyActivation | null;
  /** Why the desk did not ask for its activation at its last look. */
  activationNote?: string | null;
}

/** The desk having the provider activate an address (db/005). Open: sending · requested · unanswered. */
export interface EnergyActivation {
  id: number;
  walletId: number;
  address: string;
  state: 'sending' | 'requested' | 'unanswered' | 'refused' | 'active' | 'expired';
  costSun: string;
  countedSun: string;
  detail: string | null;
  createdAt: string;
  answeredAt: string | null;
  resolvedAt: string | null;
}

export interface EnergyPriceRow {
  purpose: EnergyPurpose;
  durationSec: number;
  energy: string;
  costSun: string | null;
  burnCostSun: string | null;
  savingBps: number | null;
  problem: string | null;
}

export interface EnergyStatus {
  fetched_at: string;
  asOf: string;
  network: 'nile' | 'mainnet';
  provider: {
    name: string;
    canOrder: boolean;
    balanceSun: string | null;
    depositAddress: string | null;
    checkedAt: string | null;
    error: string | null;
    errorAt: string | null;
    lowSun: string;
    criticalSun: string;
  };
  /** Over the last `windowSec` (24 hours), not since midnight — the limit has no midnight to be spent on both sides of. */
  spend: { windowSec: number; countedSun: string; dailyCapSun: string; orderCapSun: string };
  policy: {
    minSavingBps: number;
    minEnergy: string;
    maxEnergy: string;
    topUpDurationSec: number;
    /** The terms the desk takes for a top-up, in seconds (18 Oct 2026). Absent from a desk before it: only topUpDurationSec. */
    topUpTerms?: number[];
    standingDurationSec: number;
    standingEnabled: boolean;
  };
  /**
   * What the chain charges to burn for one unit of energy — the figure renting
   * is compared with. `at` is when it was last READ (the desk keeps a reading
   * for ten minutes, so this can be that old); `error` is set when the latest
   * read failed, and the figure beside it is then the last known one, not the
   * current one; `failingSince` is when the reads began to fail. null = not
   * read yet.
   */
  burn: { energyFeeSun: string | null; at: string | null; error: string | null; errorAt: string | null; failingSince: string | null } | null;
  prices: { at: string; rows: EnergyPriceRow[] } | null;
  wallets: EnergyWallet[];
  rentals: EnergyRental[];
  alarms: EnergyAlarm[];
  /** The desk's last pass. null = it has not completed one since it started. */
  tick: { at: string; failures: Array<{ step: string; error: string }> } | null;
  /** How far apart the desk's passes are set to be, in seconds (20 unless it was configured otherwise). */
  tickSec?: number;
  /**
   * Not the desk's: what the Books' register of treasury wallets says of each
   * wallet the desk lists (keyed by the desk's wallet id), and which of its
   * live treasury TRON wallets could be added — or why it could not be read.
   * Added by the Books (bff/src/routes/energy.ts); absent from a Books that
   * predates it, and then nothing may be added from here.
   */
  register?: EnergyRegister;
  /** Activating addresses that have never held TRX (desk db/005). Absent from a desk before it. */
  activation?: { enabled: boolean; costSun: string; perDay: number; perWalletPerDay: number; waitSec: number; retrySec: number };
  activations?: EnergyActivation[];
}

/** One live treasury TRON wallet of the Books' register. */
export interface EnergyRegisterEntry { booksWalletId: number; address: string; name: string }
/** What the register says of a wallet's address. `retiredAt` is set for one retired in the Books. */
export type EnergyRegisterVerdict =
  | { live: true; booksWalletId: number; name: string }
  | { live: false; reason: string; retiredAt: string | null };
export type EnergyRegister =
  | { wallets: Record<string, EnergyRegisterVerdict>; candidates: EnergyRegisterEntry[] }
  | { error: string };

/** A decision is an answer, not an error: `ok: false` comes with the reason. */
export interface EnergyPreview {
  ok: boolean;
  nothingToDo: boolean;
  reason: string | null;
  durationSec: number;
  energy: string | null;        // what would be ordered
  neededEnergy: string | null;  // what the wallet is short
  energyNow: { available: string; limit: string; used: string } | null;
  /** For a term of days: what the desk has rented for the wallet that lasts the whole term (18 Oct 2026). */
  lastingEnergy?: string | null;
  costSun: string | null;
  burnCostSun: string | null;
  savingBps: number | null;
}

export interface EnergyRentAnswer {
  ok: boolean;
  reason?: string;
  nothingToDo?: boolean;
  replayed?: boolean;
  rental?: EnergyRental;
}

export interface EnergyWalletAnswer {
  ok: boolean;
  reason?: string;
  wallet?: EnergyWallet;
}

// ── KYT incoming payments (GET /api/kyt/incoming, 5 Oct 2026) ───────────────
// Every level, caveat and answer below is decided by the screening worker
// (onboarding-agent src/kyt/incomingApi.ts) — the same functions its Telegram
// reports use. The screen lays them out and decides nothing.
/** `unscreened` = checked, no result (GL gave no sources to read); `pending` = read, not judged yet. */
/** `small`: under the minimum (the worker's glMinUsd — the operator, 9 Oct 2026: "$5,000"), checked on the sanctions
 *  lists only and on none of them; GL was not asked. */
export type KytLevel = 'alert' | 'watch' | 'clean' | 'unscreened' | 'pending' | 'small';
/** Where a payment is COUNTED. `unchecked` is "not fully checked": no result, not judged yet — or judged
 *  clean with something the check could not cover (the lists, the oracle, GL's sources). Never in the
 *  clean dollars. */
export type KytKind = 'clean' | 'watch' | 'alert' | 'unchecked' | 'small';
/** One sanctioned or criminal source of a payment's funds. */
export interface KytExposure {
  /** Which alert lines it was measured against. */
  tier: 'sanctions' | 'criminal';
  label: string;
  owner: string | null;
  /** Percent of the sender's funds, and the part of it received straight from the source. */
  pct: number;
  directPct: number;
  /** Dollars of THIS payment; null for a coin with no price here. */
  usd: number | null;
  /** More than one source folded into this line (the worker keeps a payment's twenty largest and one line for the rest). */
  n?: number;
}
/** How a source of funds is SHOWN. `sanctions` holds everything shown as sanctions — sanctioned parties and
 *  GL's "sanctioned exchange" alike (the operator, 5 Oct 2026). */
export type KytTraceGroup = 'sanctions' | 'criminal' | 'risky' | 'unknown' | 'known';
/** Where a wallet's funds came from: the split by kind of source, and the largest sources by name — the
 *  sanctioned and criminal ones first. A payment's trace is the picture it was RATED on, in its own dollars;
 *  a wallet's is in percents only. */
/** A criminal or sanctioned source GL listed with NO share — in no figure. `sanctions`: shown as sanctions. */
export interface KytUnmeasured { tier: 'sanctions' | 'criminal'; label: string; owner: string | null; sanctions: boolean }
export interface KytTrace {
  groups: Array<{ group: KytTraceGroup; pct: number; usd: number | null; n: number }>;
  /** `n`: a line that stands for that many sources, folded by type. */
  sources: Array<{ group: KytTraceGroup; label: string; owner: string | null; pct: number; usd: number | null; directPct: number; n?: number }>;
  /** Sources beyond those named. */
  more: number;
  /** Percent of the funds the list does not account for. */
  unread: number;
  /** Read in part, whatever `unread` says (rows unnamed or without a share, a total over the whole). */
  partial?: boolean;
  /** Criminal or sanctioned sources GL listed with no share: in no figure above. */
  unmeasured?: KytUnmeasured[];
}
export interface KytTally {
  payments: number;
  senders: number;
  usd: number;
  /** A SPLIT of the payments: each is in exactly one. */
  by: Record<KytKind, { n: number; usd: number }>;
  /** Alerts and watches (they are in `by`) whose check did not cover everything. Every payment is fully
   *  checked only when this and `by.unchecked.n` are both 0. */
  incomplete: number;
  /** Coins with no dollar price here (ETH): counted by payment. */
  coins: Array<{ asset: string; amount: number } & Record<KytKind, number>>;
  /** Dust transfers — address poisoning, never checked. */
  dust: number;
}
/** The answer before a deposit to an exchange, strongest first. `none` — the green one — needs every
 *  payment fully checked and no alert of any kind. `not_reading`: the wallet's reader has stopped, failed
 *  or never run, so "nothing found" is not known. `not_read`: no reader for this network. */
export type KytDepositAnswer = 'listed' | 'party' | 'trace_alert' | 'other_alert' | 'trace' | 'unchecked' | 'none' | 'no_payments' | 'not_read' | 'not_reading';
export interface KytIncomingWallet {
  chain: string;
  address: string;          // FULL — mask on screen
  label: string | null;
  /** Payments are read on this network (not yet: SOL). */
  read: boolean;
  reader: {
    state: 'ok' | 'never' | 'failing' | 'stale';
    since: string | null;
    ok: string | null; fail: string | null; failDetail: string | null;
    skipped: string | null; skippedAt: string | null;
  } | null;
  /** The wallet's own GL picture — what an exchange sees behind a deposit from it. */
  gl: {
    score: number; band: 'low' | 'medium' | 'high'; at: string | null;
    funds: string | null; sourcesHeld: boolean; partial: boolean;
    /** GL holds little data on the wallet: its score is not a clean bill. */
    thin: boolean;
    /** When the sources below were read, if that is an OLDER answer than the score's (the newest came without them); else null. */
    sourcesAt: string | null;
    bad: Array<{ label: string; owner: string | null; pct: number; sanctions: boolean }>;
    /** Criminal or sanctioned sources GL listed for the wallet WITHOUT a share — in no figure, by name. */
    unmeasured?: string[];
    /** The newest answer came WITH a list of sources that could not be read (the sources shown are older). */
    newestUnreadable?: boolean;
    /** The wallet's own funds by kind of source — null when its sources are not held. */
    trace?: KytTrace | null;
  } | null;
  /** The last `hours`, and since the 1st (Dubai). */
  window: KytTally;
  month: KytTally;
  /** With the clean ones, a split of the window's payments. A Watch or an Alert rated on less than the whole
   *  stays in `watch`/`alerts`/`traces`; how many such there are is the window's `incomplete`. */
  /** `small`: payments under the minimum, on no sanctions list — in the split, never "not sure" (absent on an older worker). */
  deposit: { answer: KytDepositAnswer; listed: number; traces: number; alerts: number; watch: number; unchecked: number; small?: number };
}
export interface KytPayment {
  /** Where the money goes, in KYT's words (N-011/N-024, 20 Oct 2026): apart (an alert line crossed on its own money — kept
   *  apart), clean (everything rated and under the lines — a watch and a small payment too), unknown (not rated yet, or
   *  GL gave no result). Shown, never acted on here. Absent from a worker older than this. */
  route?: 'apart' | 'clean' | 'unknown';
  id: string;
  chain: string;
  wallet: string;
  walletLabel: string | null;
  from: string;             // FULL — mask on screen
  /** The sender reads like an address the desk knows (a desk wallet, the register, a payee) but is not one — address
   *  poisoning, the worker's look-alike rule (3 Nov 2026): red on the screen, never a payee, no Copy. Absent on an older worker. */
  poisoning?: { of: string } | null;
  txid: string;
  asset: string;
  amount: number;
  usd: number | null;
  at: string | null;
  level: KytLevel;
  kind: KytKind;
  /** The minimum (dollars) this payment was under: GL was not asked (9 Oct 2026). Absent otherwise. */
  small?: number;
  /** What a "clean" could not vouch for. */
  caveats: string[];
  /** What the check did not cover, whatever the level — empty when it was fully checked or not judged yet.
   *  A watch or an alert with any of these keeps its level. */
  unread: string[];
  why: string | null;
  action: 'do_not_move' | 'keep_apart' | 'weigh_trace' | 'recheck' | 'by_hand' | null;
  score: number | null;
  band: 'low' | 'medium' | 'high' | null;
  /** The party this payment is booked to in the Books (the operator, 28 Oct 2026: "Unknown sender" says nothing; show
   *  "the linked party with whom this payment got booked"): the deal the wallet system's deposit settles (`deal`), else
   *  the Books' chain match (`booking`); null when nothing is booked. Absent on an older BFF. */
  party?: { slug: string; name: string; via: 'deal' | 'booking' } | null;
  customer: string | null;
  /** Where the person who gave this wallet in a chat stands. `blocked`: the bot blocked their chat (abuse, a
   *  scam attempt) — a Watch, never a hold: anyone can name any wallet in a chat. */
  standing: 'verified' | 'declined' | 'blocked' | 'pending' | 'unfinished' | null;
  /** The chat of the OTHER kind that named this wallet too (anyone can name any wallet): a block or a decline
   *  beside a customer; a customer or an open application beside a block or a decline. Beside, never instead. */
  also: { standing: 'blocked' | 'declined' | 'verified' | 'pending'; name: string } | null;
  /** A verified customer: true — the wallet of their approved application; false — one their chat only named. */
  own?: boolean | null;
  /** Criminal or sanctioned sources GL listed with no share: the payment is not checked; a sanctioned one is a sanctions trace. */
  unmeasured?: KytUnmeasured[];
  sanctions: {
    state: 'listed' | 'listed_before' | 'trace' | 'none' | 'unread';
    listed: string | null;
    listedBefore: string | null;
    lists: string[];
    oracleSilent: boolean;
    exposures: KytExposure[];
  };
  criminal: KytExposure[];
  risky: Array<{ label: string; pct: number }>;
  funds: string | null;
  /** The trace of the sender's funds as this payment was rated — null when no sources were read, and on a
   *  payment rated before the trace was kept. */
  trace?: KytTrace | null;
  /** This payment's place among its sender's payments into this wallet this month, and their stablecoin
   *  total up to it. */
  nth?: number | null;
  senderUsd?: number | null;
  sourcesRead: boolean;
  /** GL's sources were read for a part of the funds only — then never counted as clean. */
  partial: boolean;
  thin: boolean;
  /** Whose GL report rated it: 'tx' — this payment's own transaction (the operator chose per-transaction
   *  checks on 7 Oct 2026); 'sender' — its sender's address, as every payment before. Absent on an older worker. */
  basis?: 'sender' | 'tx';
  notes: string[];
  /** Its report reached the KYT group. */
  told: boolean;
  recheckAt: string | null;
  rechecks: number;
  /** Its deep check, when it had one (the worker's deep.ts — the operator, 8 Oct 2026). Absent on an older worker. */
  deep?: KytDeep;
}
/** A payment looked at again from the sender's side: 5% or more of its funds from sanctions or criminal sources, or a
 *  listed sender, or such a source with no share. Attribution only — the decision is the operator's. */
export interface KytDeep {
  why: 'listed' | 'share' | 'unmeasured';
  /** The payment's share from sanctions or criminal sources, in percent. */
  severePct: number;
  at: string;
  sender?: {
    score: number | null;
    band: string | null;
    /** GL's sources of the whole wallet were read (in full or in part). Absent on the first build: read. */
    read?: boolean;
    /** The sender's whole wallet by kind of source, in percent. */
    groups: Array<{ g: KytTraceGroup; pct: number }>;
    flagged: Array<{ g: KytTraceGroup; label: string; owner: string | null; pct: number | null }>;
    /** Sanctioned or criminal sources beyond those named. */
    flaggedMore?: number;
    top: Array<{ label: string; owner: string | null; pct: number }>;
    partial: boolean;
    thin?: boolean;
    /** When GL gave the answer used, if not at the deep check (a stored answer). */
    asOf?: string | null;
    note: string | null;
  };
  arkham?: { entity: string | null; labels: string[]; note: string | null };
  paid?: { n: number; usd: number };
}
export interface KytIncoming {
  fetched_at: string;
  at: string | null;
  /** The window's length in hours; with `range`, the range's span. */
  hours: number;
  /** The window as Dubai calendar days, both ends inclusive, when the read asked for a range; null or absent for hours. */
  range?: { from: string; to: string } | null;
  monthStart: string | null;
  /** The KYT group is set; until then only alerts are told, in the ops group. */
  group: boolean;
  rules: {
    /** N-011 (20 Oct 2026): a government sanctions listing alerts at sanctionsUsd AND sanctionsPct of the payment, both. */
    sanctionsUsd?: number; sanctionsPct?: number;
    sanctionsIndirectUsd: number; criminalDirectUsd: number; criminalIndirectUsd: number;
    riskyPct: number; glHigh: number; dustUsd: number; glPerDay: number;
    /** What GL is asked about a payment — the sender's address, or the payment's own transaction; absent on an older worker. */
    glBasis?: 'sender' | 'tx';
    /** The deep check's lines and the minimum for a GL check (8–9 Oct 2026); absent on an older worker. */
    deepPct?: number; deepUsd?: number; glMinUsd?: number;
    venues: Array<{ name: string; level: string }>;
  };
  wallets: KytIncomingWallet[];
  payments: KytPayment[];
  /** Payments of the period not in `payments`. */
  more: number;
  /** The Books could not be read for the parties: the payments stand without them. Absent on an older BFF. */
  partiesError?: string | null;
}

/** One row of the wallet register (routes/wallets.ts WALLET_REGISTER_SQL).
 *  `status` is the database's word — screens say lib/walletStates.ts's. */
export interface RegisterWallet {
  id: string;
  chain: string;
  address: string;
  label: string | null;
  status: 'active' | 'flagged' | 'archived' | 'unwatched';
  notes: string | null;
  first_seen_at: string;
  last_seen_at: string | null;
  retired_at: string | null;
  unwatched_at: string | null;
  chain_tx_count: number | null;
  chain_read_at: string | null;
  risk_score: number | null;
  risk_checked_at: string | null;
  /** The address follows from a Ledger account key; that key's scan keeps it. */
  from_ledger_key: boolean;
  derivation: string | null;
  party_slug: string;
  party_name: string | null;
  party_type: string;
  entity: string;
  party_status: string;
  treasury_slug: string | null;
  /** The chain sync reads it — its own status clause and a chain it reads. */
  watched: boolean;
  /** The chain reader reads this chain at all (BTC, TRC20, ERC20, BASE). */
  chain_read: boolean;
  /** What the address is for (db/0066); null = not yet classified. 'customer_payout' is an address the desk pays. */
  class: string | null;
  /** A payout address's approval (db/0066, db/0071). Payable by the wallet only when `payout_approved_via` is 'books'. */
  payout_proposed_by: string | null;
  payout_approved_at: string | null;
  payout_approved_by: string | null;
  payout_approved_via: 'books' | 'ip_command' | 'ledger_message' | null;
  /** The approval the wallet pays: the Books', by the owner (its db/0092). Absent from a server before 23 Oct 2026. */
  payout_owner_approved?: boolean;
  /** Where a payout address stands on the way to whitelisted, exactly as the wallet's admin.custody_payout_stage
   *  answers (db/0098): 'not_approved' · 'approved' · 'test_sent' (the 10 USDT test sent) · 'whitelisted'; null for a
   *  desk address. Absent from a server before db/0098. */
  payout_stage?: string | null;
}

// ── Onboarding leads (GET /api/onboarding/leads, 6 Oct 2026) ────────────────
// bff/src/routes/leads.ts proxies the onboarding worker's read-only list. The
// worker builds the list, orders it and writes every label; screens/Onboarding.tsx
// lays them out and decides nothing. The BFF passes the contract's fields and
// nothing else: a value outside the contract arrives as null — never as 0,
// 'none' or a guess — and a lead with no card code or no queue is left out, so
// the counts then say more than the list holds.
export type LeadQueue = 'stuck_no_card' | 'awaiting_decision' | 'approved_not_contacted' | 'escalation_open';
export type LeadChannel = 'business' | 'web' | 'bot-dm';
export type LeadTone = 'bad' | 'warn' | 'info' | 'good';
export type LeadLabelKey =
  | 'waiting' | 'approved_not_contacted' | 'contact_not_recorded' | 'escalated' | 'stuck' | 'chased'
  | 'wallet_severe' | 'wallet_high' | 'not_scored' | 'sanctions' | 'changed_after_card'
  | 'not_resident' | 'not_in_uae' | 'not_own_behalf' | 'over_cap' | 'no_contact_path' | 'wallet_reused'
  | 'one_id_image' | 'returning';
/** Written by the worker: the screen shows `text`, tinted by `tone`. */
export interface LeadLabel { key: LeadLabelKey; tone: LeadTone; text: string }
/** The answers that decide a lead, as the customer gave them (the card's version). */
export interface LeadFacts {
  name: string | null;
  /** Passport country, as stated. */
  nationality: string | null;
  /** A UAE residence visa or Emirates ID, as stated. */
  resident: boolean | null;
  inUae: boolean | null;
  ownMoney: boolean | null;
  /** The size in their own words. */
  size: string | null;
  /** Sells only: how they want to be paid. */
  payout: string | null;
  /** The wallet's line at its check: transactions, first seen, the USDT balance then. */
  walletActivity: string | null;
  sourceOfFunds: string | null;
  purpose: string | null;
}
/** The trade as the customer put it. */
export interface LeadClaim {
  side: 'buy' | 'sell' | null;
  asset: string | null;
  /** The AED equivalent of the first trade. 'unknown': none given; 'unclear': given, but not readable in AED (a
   *  currency the desk does not price, or a word beside the figure it cannot read) — the worker never guesses. */
  sizeBand: 'under_50k' | '50k_200k' | '200k_1m' | 'over_1m' | 'unclear' | 'unknown';
  /** 'mc': a sale paid by manager's cheque; 'developer': a sale paid to a developer. Null from an older worker. */
  type: 'buy' | 'sell' | 'mc' | 'developer' | null;
  /** The amount in AED — one figure (low = high) or a range. */
  amountAed: { low: number; high: number } | null;
}
export interface Lead {
  /** The session id — what the details are asked by. Null from a worker older than the details. */
  id: string | null;
  /** The first 8 characters of the session id — the code on the ops card, and what /contacted takes. */
  ref: string;
  queue: LeadQueue;
  channel: LeadChannel | null;
  /** '@username' only for a real Telegram username; else 'Website visitor' or 'Telegram user (no username)'. */
  who: string | null;
  /** Ops can reach them outside the bot (a @dubaiotc Business chat, or a username). */
  contactable: boolean | null;
  /** When the lead entered its queue: card posted, approved, escalated, or the last failed submit. */
  since: string | null;
  ageDays: number | null;
  lastCustomerMessageAt: string | null;
  /** Customer messages after `since`. */
  chasedTimes: number | null;
  claim: LeadClaim;
  walletCheck: {
    /** 'none' = no check on record. */
    status: 'clean' | 'flagged' | 'unscreened' | 'error' | 'pending' | 'none' | null;
    band: 'low' | 'medium' | 'high' | 'severe' | null;
    /** 0–100, Global Ledger. */
    score: number | null;
    sanctions: 'listed' | 'clear' | 'not_checked' | null;
    /** Who said so: the list that named the address, else the lists the check read ("OFAC SDN (TRX, USDT) · Chainalysis
     *  oracle"); null when not known. And when the OFAC lists it read were refreshed, when known. */
    sanctionsSource: string | null;
    sanctionsListAt: string | null;
    /** The wallet's USDT balance at its check. */
    balanceUsdt: number | null;
    /** Whether that balance covers the USDT they said they would sell; null when it does not apply. */
    coversClaim: boolean | null;
  };
  /** escalation_open only: the reason code given when it was raised. */
  escalationReason: string | null;
  labels: LeadLabel[];
  facts: LeadFacts;
  /** The card's caveats as posted — none before a card. */
  caveats: string[];
  /** Its card (or escalation) in the ops group — opens for the group's members only. */
  cardUrl: string | null;
  /** Their chat: https://t.me/<username> or https://wa.me/<number>; null when no link opens it. */
  contactUrl: string | null;
  /** The app the chat button opens; null = no way to reach them. */
  contactApp: 'telegram' | 'whatsapp' | null;
  /** The link was typed by the visitor in the web chat — not verified. */
  contactGiven: boolean;
  /** A Telegram account with no username: the chat button asks POST /api/onboarding/chat-link. */
  chatViaBot: boolean;
}
/** One lead's whole record (onboarding worker src/admin/leadDetails.ts). */
export interface LeadDetails {
  id: string;
  ref: string | null;
  state: 'new' | 'awaiting_approval' | 'kyc_pending' | 'done' | 'rejected' | null;
  channel: LeadChannel | null;
  who: string | null;
  name: string | null;
  /** Their customer record in the onboarding worker — what a party is linked to; null before one exists, or from a
   *  worker older than it (15 Oct 2026, D3). */
  customerId: string | null;
  /** The Books party made from, or linked to, this customer (admin.parties.onboarding_customer_id); null: none yet. */
  party: PartyLink | null;
  /** Their chat: https://t.me/<username> or https://wa.me/<number>; null when no link opens it. */
  contactUrl: string | null;
  /** The app the chat button opens; null = no way to reach them. */
  contactApp: 'telegram' | 'whatsapp' | null;
  /** The link was typed by the visitor in the web chat — not verified. */
  contactGiven: boolean;
  /** A Telegram account with no username: the chat button asks POST /api/onboarding/chat-link. */
  chatViaBot: boolean;
  cardUrl: string | null;
  /** As the card showed them (as given, before a card). */
  answers: {
    side: 'buy' | 'sell' | null;
    asset: string | null;
    size: string | null;
    sizeBand: Lead['claim']['sizeBand'];
    frequency: string | null;
    payout: string | null;
    nationality: string | null;
    jurisdiction: string | null;
    resident: boolean | null;
    inUae: boolean | null;
    ownMoney: boolean | null;
    sourceOfFunds: string | null;
    purpose: string | null;
    referredBy: string | null;
  };
  /** What the customer has said differently since the card. */
  changed: { field: string; card: string | null; now: string | null }[];
  wallet: {
    address: string | null;
    chain: string | null;
    origin: string | null;
    status: Lead['walletCheck']['status'];
    band: Lead['walletCheck']['band'];
    score: number | null;
    sanctions: Lead['walletCheck']['sanctions'];
    sanctionsSource: string | null;
    sanctionsListAt: string | null;
    activity: string | null;
    checkedAt: string | null;
  } | null;
  checks: { ok: boolean; label: string; note: string | null }[];
  caveats: string[];
  idImages: number | null;
  priorContact: string[];
  /** The last messages both ways, oldest first. */
  chat: { from: 'customer' | 'bot'; at: string | null; text: string }[];
  /** Card, escalations, decisions, contact and handling, oldest first. */
  timeline: { at: string | null; what: string; by: string | null }[];
  fetched_at: string;
}
export interface ApprovedClient {
  /** The session id, when they applied through the bot — what their details are asked by. */
  id: string | null;
  ref: string | null;
  /** approved_here: approved on a card here; imported: a verified client record brought over from before. */
  source: 'approved_here' | 'imported';
  who: string | null;
  name: string | null;
  /** The Books party made from, or linked to, this client (15 Oct 2026, D3); null: none yet. */
  party: PartyLink | null;
  channel: LeadChannel | null;
  approvedAt: string | null;
  approvedBy: string | null;
  contacted: { at: string | null; by: string | null } | null;
  /** When the record was first seen — an imported client's only date. */
  since: string | null;
  facts: LeadFacts | null;
  /** The trade, from the client's session — null for a record with none (most imported ones). */
  claim: LeadClaim | null;
  summary: string | null;
  cardUrl: string | null;
  /** Their chat: https://t.me/<username> or https://wa.me/<number>; null when no link opens it. */
  contactUrl: string | null;
  /** The app the chat button opens; null = no way to reach them. */
  contactApp: 'telegram' | 'whatsapp' | null;
  /** The link was typed by the visitor in the web chat — not verified. */
  contactGiven: boolean;
  /** A Telegram account with no username: the chat button asks POST /api/onboarding/chat-link. */
  chatViaBot: boolean;
  /** The client record's id — set on an imported record, which may have no session (`id` null): its chat button is
   *  asked by this. */
  customerId: string | null;
  /** The bot's refusal of this person since the approval, a linked chat included — for an imported record, any
   *  refusal. Shown, never acted on: "Operator word is final" (operator, 7 Oct 2026). Null: none. */
  refusedByBot: BotRefusal | null;
}
/** The bot's reasons for a refusal, as its codes; 'other' is its own word for the rest. */
export type BotRefusalReason =
  | 'below_minimum' | 'residency' | 'tourist_buy' | 'tourist_not_tier1' | 'unsupported_request' | 'sanctioned_passport'
  | 'third_party' | 'anonymous' | 'exceeds_first_trade_max' | 'previously_declined' | 'other';
export interface BotRefusal {
  at: string | null;
  /** At most 5; null when the worker's list was not one. */
  reasons: BotRefusalReason[] | null;
  /** True: a compliance refusal (the latest, when there is one); false: a decline; null: not known. */
  compliance: boolean | null;
}
export interface ApprovedClients {
  at: string | null;
  /** When contacts began to be recorded — the first approval with the Mark contacted button; null until then. An
   *  approval before it with no contact on record reads "Contact not recorded", never "Not contacted". */
  contactTrackingSince: string | null;
  counts: { approvedHere: number | null; imported: number | null };
  page: { offset: number; size: number } | null;
  clients: ApprovedClient[];
  fetched_at: string;
}
/** A potential lead (GET /api/onboarding/prospects): still in the chat with the bot, said a side or a size, not
 *  refused, nothing with the team yet — no card, escalation or tried submission (those are the New leads queues). */
export interface Prospect {
  /** The session id — what its record is asked by. */
  id: string | null;
  ref: string;
  channel: LeadChannel | null;
  /** '@username' only for a real Telegram username; else 'Website visitor' or 'Telegram user (no username)'. */
  who: string | null;
  name: string | null;
  claim: LeadClaim & { asset: 'USDT' | 'USDC' | 'BTC' | 'ETH' | 'Other' | null };
  /** The answers that decide a lead, as given so far. */
  facts: LeadFacts;
  /** The intake questions still unanswered, by id (onboarding-agent src/onboarding/intake.ts); null when not known. */
  missing: string[] | null;
  lastCustomerMessageAt: string | null;
  customerMessages: number | null;
  /** The last message either way — the list's order. */
  lastActiveAt: string | null;
  /** Their chat: https://t.me/<username> or https://wa.me/<number>; null when no link opens it. */
  contactUrl: string | null;
  /** The app the chat button opens; null = no way to reach them. */
  contactApp: 'telegram' | 'whatsapp' | null;
  /** The link was typed by the visitor in the web chat — not verified. */
  contactGiven: boolean;
  /** A Telegram account with no username: the chat button asks POST /api/onboarding/chat-link. */
  chatViaBot: boolean;
}
/** Where the chat button opens, from POST /api/onboarding/chat-link: `link` their own chat; `bot_dm` the bot's chat,
 *  where it has just sent the signed-in admin a one-tap button to theirs; `ops_group` that button's message in the ops
 *  group (the bot could not write to the admin). */
export interface ChatLink { url: string; app: 'telegram' | 'whatsapp'; via: 'link' | 'bot_dm' | 'ops_group' }
/** A Books party, as the Onboarding screens name it: its slug (the page's address), its name and its desk. */
export interface PartyLink { slug: string; name: string; entity: 'dubaiotc' | 'indiaotc' }
/** The answer to "Make a party": the party, and whether this call made it (false: it already existed, or was linked). */
export interface MadeParty { party: PartyLink; made: boolean }
/** "Make a party" refused because a party of that name already exists: the existing one, to link to or to pass over. */
export interface PartyNameTaken { error: 'name_taken'; reason: string; existing: PartyLink }
/** The worker's counts by key, `other` summing the keys the screen has no words for. Null: not known — never 0. */
export type FunnelCounts<K extends string> = Record<K | 'other', number | null>;
export interface OnboardingProspects {
  at: string | null;
  /** The last 24 hours: chats with a message either way, the bot's replies; and the bot's last reply ever. */
  activity: { chats24h: number | null; replies24h: number | null; lastReply: string | null };
  /** Chats by state, and client records by status — the funnel. */
  states: FunnelCounts<'new' | 'awaiting_approval' | 'kyc_pending' | 'done' | 'rejected'>;
  customers: FunnelCounts<'unverified' | 'pending_review' | 'verified' | 'rejected'>;
  /** Every potential lead, not only the page listed. */
  total: number | null;
  page: { offset: number; size: number } | null;
  /** The most recently active first (a message either way). */
  prospects: Prospect[];
  /** The BFF's clock when it read the worker — every age on the screen is measured against it. */
  fetched_at: string;
}
export interface OnboardingLeads {
  /** When the onboarding worker read it. */
  at: string | null;
  /** Records the worker could not build — a stored record it found malformed: counted, never listed.
   *  Null from a worker that sends no whole number for it: not known — never read as 0. */
  unreadable: number | null;
  counts: { awaitingDecision: number | null; approvedNotContacted: number | null; escalationsOpen: number | null; stuck: number | null };
  /** The oldest card still waiting for a decision; null when none is waiting. */
  oldestWaitingDays: number | null;
  /** When the "Mark contacted" button went live: an approval before it reads "contact not recorded". */
  contactTrackingSince: string | null;
  /** What is listed: every queue's first `size` (queue null), or one queue's from `offset`. Null from a worker
   *  older than paging. */
  page: { queue: LeadQueue | null; offset: number; size: number } | null;
  /** At most `page.size` per queue, in the worker's order: by queue, then the most `bad` labels, then the oldest. */
  leads: Lead[];
  /** The BFF's clock when it read the worker — every age on the screen is measured against it. */
  fetched_at: string;
}

// =========================================================================
// Custody — the wallet system on main's model (docs/WALLET-REBUILD.md).
// Every address is a row of the register with a CLASS; the pipeline's pools
// (clean / dirty / quarantine) are the floats' classes seen from screening.
// =========================================================================

export type Pool = 'clean' | 'dirty' | 'quarantine';
export type Freshness = 'fresh' | 'stale' | 'blind';
export type DeskClass = 'deposit' | 'running_hot' | 'dirty_hot' | 'frozen' | 'treasury' | 'fee';

export interface WalletBalance {
  id: number;
  /** The register's class. */
  class: DeskClass;
  pool: Pool | null;
  chain: string;
  address: string;
  /** The register's label — the month the address came into use. */
  label: string | null;
  /** The wallet's one name (N-091, bff/src/lib/wallet-name.ts) — `full` is "Hot · TRC20 · Nov". Absent from an older server. */
  name?: { full: string; line1: string; list: string } | null;
  asset: string;
  /**
   * null = NOT OBSERVED, which is not zero. A per-address balance is a fact
   * about the chain, read by the watcher into a cache; with no reading, or
   * one too old to present as current, this is null and callers must skip
   * it. Rendering an unread balance as 0.00 is how an operator concludes a
   * wallet is empty when it is merely unreachable.
   */
  balance: string | null;
  asOf: string | null;
  freshness: Freshness;
  signing: 'ledger' | 'multisig_2of3' | 'server';
  /** For a deposit address: the customer it was issued to. */
  partySlug?: string | null;
}

export interface DepositRow {
  id: number;
  /** The deposit's key at KYT, with txHash and logIndex. Absent on an older server. */
  chain?: string;
  txHash: string;
  logIndex: number;
  fromAddress: string;
  asset: string;
  amount: string;
  status: 'detected' | 'confirmed' | 'screening' | 'held' | 'routed' | 'quarantined' | 'rejected' | 'swept';
  /**
   * Still waiting on someone, of any age: on its way (detected → routed), Flagged (status 'quarantined') with its case open, or in Merchant
   * Hot and not yet released. /deposits with no filter returns these whatever their age, plus the last 24 hours;
   * ?status=open returns only these. Absent = an older server.
   */
  open?: boolean;
  pool: Pool | null;
  dealId: string | null;
  partySlug: string | null;
  partyName: string | null;
  /** The address it came in on: `id` is what custody.tagDepositAddress takes. Absent on an older server. */
  depositAddress?: { id: number; address: string };
  /**
   * Its Books entry (the deposit event of its sweep): null = not queued yet. `why`: untagged = waits for a tag;
   * desk_money_mixed = desk and customer money in one transfer, booked by hand; desk_round_trip = settled as the desk's
   * own money, no entry; other = the Books' own reason; null = nothing to say. Absent on an older server.
   */
  books?: {
    status: 'pending' | 'booked' | 'refused';
    why: 'untagged' | 'desk_money_mixed' | 'desk_round_trip' | 'other' | null;
    byHand: boolean;
    bookedAt: string | null;
  } | null;
  /** The payment's own rating (N-011, 20 Oct 2026): clean → Hot, exposed → Merchant Hot, pending → held. Older rows carry the old words. */
  kytVerdict?: 'clean' | 'exposed' | 'pending' | 'clear' | 'review' | 'flagged' | 'error' | null;
  /** The sentences that say why — the engine's sources over the operator's lines. */
  kytBecause?: string[];
  /** A source GL named without a figure: Merchant Hot until checked by hand (N-032). */
  kytHandCheck?: boolean;
  kytRatedAt?: string | null;
  /** The lists, as INFORMATION (the operator, 22 Oct 2026: never a refusal): the sender named on a government list, blacklisted by a venue, listed money through. Absent on an older Worker. */
  kytLists?: {
    senderListed: { listing: string; label: string } | null;
    blacklisted: Array<{ by: string; address: string | null }>;
    sanctions: Array<{ listing: string; label: string; usd: number | null }>;
  } | null;
  /**
   * Every KYT fact the intake kept (N-097, bff/src/db/custody.ts kytOf): `result` is where the money went by the wallet's
   * lines; `view.level` is KYT's attention word — they differ by design, so a screen says both, apart. `view` is absent
   * on a deposit rated before KYT's release. null = never rated; absent = an older server.
   */
  kyt?: {
    paymentId: string | null;
    result: 'clean' | 'exposed' | 'pending' | null;
    finding: { heading: string; evidence: string | null } | null;
    basis: string | null;
    paymentUsd: number | null;
    criminal: Array<{ category?: string; label?: string; owner?: string | null; usd?: number | null; directUsd?: number | null; indirectUsd?: number | null; sharePct?: number | null }>;
    sanctions: Array<{ listing?: string; label?: string; owner?: string | null; usd?: number | null; sharePct?: number | null }>;
    unmeasured: Array<{ tier?: string; label?: string; owner?: string | null }>;
    pending: boolean;
    checkedAt: string | null;
    ratedAt: string | null;
    view: {
      level?: 'alert' | 'watch' | 'clean' | 'unscreened' | 'small';
      action?: 'do_not_move' | 'keep_apart' | 'weigh_trace' | 'recheck' | 'by_hand' | null;
      finding?: { head: string; source: string | null; why: string | null };
      sender?: { name: string | null; label: string | null } | null;
      gl?: { score: number | null; band: string | null };
      trace?: { groups: Array<{ group: string; pct: number; usd: number | null; n: number }>; unread: number | null; partial: boolean } | null;
      lists?: { read: string[]; refreshedAt: string | null };
      /** The sender reads like an address the desk knows but is not one — address poisoning (KYT's door, 3 Nov 2026; the
       *  worker's look-alike rule). Red on the screen, never a payee, no Copy. Absent on an older worker. */
      poisoning?: { of: string } | null;
    } | null;
  } | null;
  detectedAt: string;
  /** The steps the money took, with their times (21 Oct 2026); absent on an older Worker. */
  confirmedAt?: string | null;
  screenedAt?: string | null;
  heldAt?: string | null;
  /** The sweep that carries it once routed: queued (no card yet), awaiting_approval (a card), energizing, broadcast (sent), swept (done), failed, expired. */
  sweep?: {
    id: number; status: string; builtAt: string | null; broadcastAt: string | null; completedAt: string | null; txHash: string | null;
    /** Its latest card (pay sheet round 8): open = status awaiting_cosign and not expired. null = none built; absent = an older Worker. */
    card?: { txid: string; status: string | null; expiresAt: string | null } | null;
  } | null;
  /**
   * Its latest Release (db/0105, N-099): the Merchant Hot → Hot move that releases it, its status as a move (MoveStatus;
   * 'expired' = the card lapsed and is rebuilt next window — still open) and its latest card. null = never pressed, or
   * absent on an older server. Release may be pressed when pool is 'dirty', status 'swept', and this is null or failed.
   */
  release?: {
    moveId: number; status: MoveStatus;
    card: { txid: string; status: string | null; expiresAt: string | null } | null;
    txHash: string | null; completedAt: string | null;
  } | null;
}

export interface ApprovalRow {
  txid: string;
  kind: 'sweep' | 'payout' | 'refill' | 'vault_move' | 'quarantine_move' | 'renewal_move' | 'release';
  fromAddress: string;
  /** null when the stored transaction is malformed — render a refusal, not a blank. */
  toAddress: string | null;
  partyName: string | null;
  asset: string;
  /** null when the stored transaction is malformed. Never render as 0. */
  amount: string | null;
  pool: Pool | null;
  expiresAt: string;
  signaturesCollected: number;
  signaturesRequired: number;
  /** Who queued the move (telegram id); null for the desk's own sweeps. Absent on an older Worker. */
  proposedBy?: string | null;
  /** The deal a payout settles (db/0111, P-063); null when none was named. Absent on an older Worker. */
  dealId?: string | null;
  /**
   * The second device's word on a payout (N-068): `required` once two devices
   * are registered; `by` and `at` once given. Absent on an older Worker.
   */
  approval?: { required: boolean; by: string | null; at: string | null };
}

export interface WhitelistEntry {
  id: number;
  partySlug: string;
  partyName: string;
  chain: string;
  address: string;
  label: string | null;
  status: 'active' | 'flagged' | 'archived' | 'unwatched';
  proposedBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  /** How it became payable: 'ip_command' (one command from the allowed IP) or 'ledger_message' (a second person's Ledger). */
  /** 'books' is the only word that makes it payable (22 Oct 2026); the others are proposals. */
  approvedVia: 'books' | 'ip_command' | 'ledger_message' | null;
  /** The payee's standing in the Books: a customer, or a party marked MERCHANT; absent on an older Worker. */
  partyRole?: 'customer' | 'merchant' | null;
  /** Payable: approved in the Books BY THE OWNER (contracts payoutApprovedByOwner); absent on an older Worker, which checked the word alone. */
  approvedByOwner?: boolean;
  firstPayoutAt: string | null;
  /**
   * The whitelist's stage (db/0098 admin.custody_payout_stage — one rule, in the database): approved (only the
   * 10 USDT test can be paid) → test_sent (on its way, or landed and waiting for Received) → whitelisted.
   * not_approved before the owner's Books approval. Absent from an older server: the screen falls back to the approval.
   */
  stage?: 'not_approved' | 'approved' | 'test_sent' | 'whitelisted' | null;
  /** When the test landed: the stamp, or the first swept payment before it is stamped (db/0098). Received is drawn from this. */
  landedAt?: string | null;
  /** The operator's Received on the test (db/0098): when, and who. */
  testReceivedAt?: string | null;
  testReceivedBy?: string | null;
  /** The test's transaction, once it landed. */
  testTxHash?: string | null;
  /** The status of a payment to this address still on its way, if any. */
  openStatus?: string | null;
  /**
   * One of the desk's own wallets outside custody (item 6: Hot · TRC20 · Oct beside the custody Hot), held to the
   * same whitelist and paid from Hot only; false for a customer's or merchant's address. Absent on an older server.
   */
  desk?: boolean;
  /** The register's class: customer_payout, or a desk wallet's (running_hot, dirty_hot, treasury, frozen). */
  class?: string;
  /** A desk wallet's one name (N-091: "Hot · TRC20 · Oct"); null for a customer's address. Absent on an older server. */
  name?: { full: string; line1: string; list: string } | null;
  kytVerdict: string | null;
  kytExpiresAt: string | null;
}

// ---- the operator-executable flow (17 Oct 2026) ----------------------------

/** The open approval window: the operator is at the desk until expiresAt. */
export interface ApprovalWindow {
  id: number;
  /** Who opened it — a Telegram id. */
  openedBy: string;
  openedAt: string | null;
  expiresAt: string;
}

/** A Ledger account's public key registered with the desk — one deposit address per deal comes from it. */
export interface CustodyKey {
  id: number;
  chain: string;
  /** 'bip44-tron' for the Ledger's TRON account key; the register's other schemes are Bitcoin's and Ethereum's. */
  scheme: string;
  label: string | null;
  /** The TRON account on the device, numbered from 0 as Ledger Live does; null when the key does not say. */
  account: number | null;
  /** What the account issues, by the desk's one convention (contracts KEY_ACCOUNT_CLASS: 0 deposit, 1 running_hot, 2 dirty_hot, 3 treasury; custody_key_owners.class); null for an account the desk has no use for. Absent on an older Worker. */
  class?: string | null;
  /** That class, as it is said (CLASS_WORDS) — "Hot", "Treasury"; null with `class`. Absent on an older Worker. */
  classWord?: string | null;
  /** The address at …/<account>'/0/0 — what the device showed when the key was registered; null when it cannot be derived. */
  firstAddress: string | null;
  /** The last deposit index issued under this key; -1 before the first. */
  lastIndex: number;
  status: string;
  registeredAt: string | null;
  /** Who registered it, and the device that owner signs with (a public address); absent on an older Worker. */
  ownerTelegramId?: number | null;
  device?: string | null;
}

/** What the dashboard posts to register a key — the key export from cosign/src/ledger.ts, as hex. */
export interface RegisterKeyBody {
  chain: 'TRC20';
  account: number;
  /** Accounts 1–3: the registered device's signature over the proof transfer it showed — 0 TRX from itself to the key's first address, never broadcastable (signer/src/key-approval.mjs); the server refuses the key without it (key_not_proven). */
  proof?: { rawDataHex: string; signature: string };
  /** 65 bytes of hex, 04 then X and Y. */
  publicKey: string;
  /** 32 bytes of hex. */
  chainCode: string;
  firstAddress: string;
  /** The fingerprint of m/44'/195', eight hex characters, as the export answers it; the server derives it when absent. */
  parentFingerprint?: string;
  label?: string;
}

/** One deposit address the bot issued: a register row of class deposit under the account key. */
export interface DepositAddress {
  id: number;
  address: string;
  partySlug: string | null;
  partyName: string | null;
  dealId: string | null;
  /** The register's label — the month the address was issued. */
  label: string | null;
  /** The register's path under the account key: "0/<index>". */
  derivation: string | null;
  issuedAt: string | null;
  /** The operator's own lines about it ("ali 5k"), dated, one per line. */
  notes?: string | null;
}

/**
 * A card as the signing page is told it (cosign/src/main.ts `Approval`), plus
 * the source's derivation. Every field the dashboard shows about the
 * transaction it decodes from `rawDataHex` itself, with the page's functions;
 * `to`, `amount` and `source.address` are the server's claims, held against
 * those bytes.
 */
export interface SigningCard {
  kind: string;
  rawDataHex: string;
  tx: unknown;
  amount?: string;
  to?: string;
  /**
   * The wallet the money leaves, as the desk registered it. `derivation` is
   * the register's path under its key ("0/7"); `account` is WHICH Ledger
   * account that key is (slice E: a renewed Hot address is issued from
   * account 1, Merchant Hot from 2, Treasury from 3 — contracts
   * KEY_ACCOUNT_CLASS), so the cable signer asks that account first. Null or
   * absent for a hand-registered row, and on an older Worker.
   */
  source?: { address?: string; name?: string; layer?: string; derivation?: string | null; account?: number | null };
  /** The desk's entry for the destination — one entry, or null. Read through parseDestination. */
  destination?: unknown;
  /** The key the server takes a signature from, or null when none the operator holds can sign. Read through parseSigner. */
  signer?: unknown;
}

/** The server's answer once it took a signature — the signing page's, word for word (cosign/src/status.ts reads it). */
export interface SignatureAnswer {
  ok: boolean;
  broadcast: boolean;
  txId: string;
  signaturesCollected: number;
  signaturesRequired: number;
}

// ---- the move caps (21 Oct 2026) --------------------------------------------

/** The caps as GET /api/custody/limits answers them (bff/src/db/custody-limits.ts LimitsOut). */
export type FeeFloorClass = 'running_hot' | 'dirty_hot' | 'frozen' | 'treasury';
/** The TRX each float keeps for its own chain fees, refilled from the fee wallet under half (the operator's decision 1, 23 Oct 2026). */
export interface FeeFloors {
  floors: Record<FeeFloorClass, string>;
  /** He set them; false while the seeded defaults stand. */
  set: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Under this much TRX a source is refused a new payout before the Nano. */
  minTrx: string;
}

export interface MoveLimits {
  moveCeilingUsdt: string;
  dayCeilingUsdt: string;
  /** The most one payout may carry to an address no payout has reached yet. */
  firstPayoutCeilingUsdt: string;
  /** The whitelist's test (db/0098), in USDT; absent from an older server (10). */
  testPaymentUsdt?: string;
  /** No caps (db/0098): false while testing — the caps are enforced. Absent from an older server: false. */
  capsOff?: boolean;
  usedTodayUsdt: string;
  /** Midnight of the Dubai day the figure counts from. */
  dayStartsAt: string;
  /** The two-device gate's switch (db/0088): a payout waits for the second device only while true. Off by default. */
  secondDevicePayouts: boolean;
  /** The two sources that pay merchants only, each under its own caps within Hot's day (db/0089). */
  sources: Record<CappedSource, { moveCeilingUsdt: string; dayCeilingUsdt: string; usedTodayUsdt: string }>;
  updatedAt: string | null;
  updatedBy: string | null;
}
/** Where a move may leave from: Hot pays any approved address; Merchant Hot and Flagged pay merchants only (the operator, 22 Oct 2026); the vault only refills Hot. */
export type PayoutSource = 'running_hot' | 'dirty_hot' | 'frozen' | 'treasury';
/** A move row's status (db/0076): queued … energizing wait for or follow the signature; broadcast and swept are sent; failed and expired are not paid. */
export type MoveStatus = 'queued' | 'activating' | 'awaiting_approval' | 'energizing' | 'broadcast' | 'swept' | 'failed' | 'expired' | (string & {});
export type CappedSource = 'dirty_hot' | 'frozen';

// ---- renewals (slice E, 18 Oct 2026) ---------------------------------------
//
// The three floats renew themselves on the operator's intervals (17 Oct 2026:
// Merchant Hot every 2–3 days, Hot every 15, Treasury every 30): the
// orchestrator issues the next address from the class's own Ledger account,
// retires the old row — still watched, still read by the Books — and the old
// balance moves to the new address only when the operator signs the move on
// his Ledger (18 Oct 2026: never automatically). This is what the dashboard
// reads and sets about that; the renewing itself is the orchestrator's.

/** The classes that renew (contracts RENEWABLE_CLASSES). */
export type RenewalClass = 'running_hot' | 'dirty_hot' | 'treasury';

/** One class's policy row, as the two POSTs answer it (bff/src/db/custody-renewals.ts RenewalPolicyOut). */
export interface RenewalPolicy {
  class: RenewalClass;
  /** The class, said (CLASS_WORDS): Hot, Merchant Hot, Treasury. */
  classWord: string;
  /** The policy: renew every N days, 1..365. */
  everyDays: number;
  /** The operator's own interval for the class (contracts RENEWAL_DEFAULT_DAYS), for a reset. */
  defaultDays: number;
  lastRenewedAt: string | null;
  nextDueAt: string | null;
  /** Whole days until nextDueAt, rounded up; 0 when it is due (the next tick renews); null without a date. */
  dueInDays: number | null;
  updatedAt: string | null;
  /** A Telegram id, 'orchestrator', or null. */
  updatedBy: string | null;
}

/** The move of a retired address's balance to the class's current address that is still in flight. */
export interface RenewalOpenMove {
  sweepId: number;
  status: string;
  /** Decimal USDT. */
  amount: string;
  createdAt: string | null;
}

/** What a key still signs (GET /api/custody/keys/:id/holdings): every address it issued, however old, read or not. */
export interface KeyHoldings {
  keyId: number;
  addresses: Array<{ id: number; class: string; address: string; status: string; label: string | null; balance: string | null; asOf: string | null; readError: string | null; freshness: Freshness }>;
  /** The sum of the CURRENT readings, or null when none is current. */
  total: string | null;
  read: number;
  /** Every address has a current reading. */
  complete: boolean;
}

/** A retired address of a renewable class (the last 90 days), as GET /api/custody/renewals lists it under its class. */
export interface RenewalRetiredRow {
  walletId: number;
  address: string;
  label: string | null;
  /** Its one name, retired too (N-091) — "Merchant Hot · TRC20 · Oct". Absent from an older server. */
  name?: { full: string; line1: string; list: string } | null;
  retiredAt: string | null;
  /** Its last USDT reading, a decimal string; null when never read. */
  balance: string | null;
  balanceAsOf: string | null;
  readError: string | null;
  /** The move of its balance to the class's current address that is still in flight (a custody_sweeps row not swept, failed or expired), or null. */
  openMove: RenewalOpenMove | null;
  /** Whether a key the operator holds signs for it; false = moved by hand. Absent on an older Worker. */
  signable?: boolean;
}

/** One renewable class, as GET /api/custody/renewals answers it. */
export interface RenewalRow extends RenewalPolicy {
  /** The Ledger account this class renews from (KEY_ACCOUNT_CLASS), numbered from 0. */
  keyAccount: number;
  /** Whether an active key for that account is registered — without one the orchestrator cannot renew, and says so. */
  keyRegistered: boolean;
  /** The id of that key, or null. */
  keyId: number | null;
  /** The address in use now, or null when the class has none on the chain. */
  active: {
    walletId: number; address: string; label: string | null;
    /** Its one name (N-091) — "Merchant Hot · TRC20 · Nov". Absent from an older server. */
    name?: { full: string; line1: string; list: string } | null;
    issuedAt: string | null;
  } | null;
  retired: RenewalRetiredRow[];
}

export interface CustodySummary {
  depositsToday: number;
  payoutsToday: number;
  pendingApprovals: number;
  openCases: number;
  feeTrx: string | null;
  feeAsOf: string | null;
  asset: string;
  /** Whether an approval window is open now, and until when. */
  window: { open: boolean; expiresAt: string | null };
  /** The Fee float as the wallet server's last check saw it (db/0110, P-072): null = not known (no check in two of its
   *  rounds, or db/0110 not applied); absent on an older Worker. activationsLeft null = the check could not price one. */
  feeFloat?: { condition: string; activationsLeft: number | null; checkedAt: string } | null;
  /** The TRON solid head as the wallet server last knew it: stalled = every provider at one block 30 min or more (the
   *  bot's "TRON reads stalled"), since = when it last moved. null = not known; absent on an older Worker. */
  tron?: { stalled: boolean; since: string | null; block: number | null; checkedAt: string } | null;
}

/** One move on a desk wallet, as GET /api/custody/wallets/:id/moves lists it (P-073), newest first. */
export interface WalletMove {
  kind: 'move' | 'deposit';
  /** The custody move's id, or the deposit's. */
  id: number;
  direction: 'in' | 'out';
  /** The other end: a desk wallet by its register name, or a sender's address (name null). */
  other: { address: string; name: string | null; class: string | null };
  asset: string;
  /** Decimal string. */
  amount: string;
  /** A move: queued … swept / failed / expired. A deposit: detected … swept / rejected. */
  status: string;
  txHash: string | null;
  at: string;
}

export const custody = {
  /** One desk wallet's moves and the deposits that landed on it, newest first (P-073); 404 for anything but a desk wallet. */
  walletMoves: (walletId: number, limit?: number) => request<{ wallet: { id: number; class: string; address: string; name: string | null }; moves: WalletMove[] }>(
    `/api/custody/wallets/${walletId}/moves${limit ? `?limit=${limit}` : ''}`),
  me:         () => request<{ telegramId: number; role: string; entityScope: string[]; device: string | null; chain: string }>('/api/custody/me'),
  balances:   () => request<{ wallets: WalletBalance[]; asOf: string | null; freshness: Freshness; unreadable: number }>('/api/custody/balances'),
  summary:    () => request<CustodySummary>('/api/custody/summary'),
  /** No status: the open deposits of any age plus the last 24 hours. 'open': only the open ones. A status: that status, newest 200. */
  deposits:   (status?: DepositRow['status'] | 'open') => request<{ deposits: DepositRow[] }>(`/api/custody/deposits${status ? `?status=${status}` : ''}`),
  approvals:  () => request<{ approvals: ApprovalRow[] }>('/api/custody/approvals'),
  /**
   * The OTHER device's approval of a payout card (N-068): the registered
   * device's signature over the approval message (signer/src/card-approval.mjs).
   * Refused: approval_not_needed (not a payout), self_approval_refused (the
   * proposer), same_device_refused (the device that signed the transaction),
   * no_registered_device, signature_not_authorized, already_approved.
   */
  approveCard: (txid: string, body: { message: string; signature: string }) =>
    request<{ ok: true; txId: string; approvedBy: string }>(`/api/custody/approvals/${encodeURIComponent(txid)}/approval`, { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Mint a signing link for one approval, at the moment the operator asks for
   * it: the 15-minute clock starts at intent, no credential is created for an
   * approval nobody opened, and the token stays out of a DOM href. Only the
   * token is kept from the server's answer; the link is rebuilt on the signing
   * site fixed in this build (lib/cosign-link.ts).
   */
  cosignLink: (txid: string) =>
    mintedLink(COSIGN_ORIGIN, { kind: 'approval', txid }, () =>
      request<{ url: string; expiresAt: string }>(`/api/custody/approvals/${encodeURIComponent(txid)}/cosign-link`, { method: 'POST' })),
  whitelist:  () => request<{ entries: WhitelistEntry[] }>('/api/custody/whitelist'),
  customers:  () => request<{ customers: Array<{ slug: string; name: string; role?: 'customer' | 'merchant' }> }>('/api/custody/customers'),
  /** Mint a hardware-approval link for a proposed payout address — by someone other than its proposer. */
  whitelistApprovalLink: (id: number) =>
    mintedLink(COSIGN_ORIGIN, { kind: 'whitelist', id }, () =>
      request<{ url: string; expiresAt: string }>(`/api/custody/whitelist/${id}/approval-link`, { method: 'POST' })),
  proposeWhitelist: (body: { partySlug: string; chain: string; address: string; label?: string }) =>
    request<{ ok: boolean; id: number; kytVerdict: string | null }>('/api/custody/whitelist', { method: 'POST', body: JSON.stringify(body) }),
  issueDepositAddress: (slug: string) =>
    request<{ address: string; chain: string; derivationPath: string | null; reused: boolean }>(`/api/custody/parties/${encodeURIComponent(slug)}/deposit-address`, { method: 'POST' }),

  // ---- the operator-executable flow (17 Oct 2026) --------------------------
  //
  // Signing in the dashboard: the card the signing page would be shown, and
  // the signature the Ledger made over it by cable. The same checks and the
  // same compare-and-swap as the link's routes — one implementation on the
  // server, two doors — with the operator's own sign-in and no link token.
  /** Exactly what the signing page is told about this card, plus the source's derivation. */
  card: (txid: string) =>
    request<SigningCard>(`/api/custody/approvals/${encodeURIComponent(txid)}/card`),
  /** The signature the Ledger made over the card's bytes; the page's answers, word for word. */
  submitSignature: (txid: string, signature: string) =>
    request<SignatureAnswer>(`/api/custody/approvals/${encodeURIComponent(txid)}/signature`, {
      method: 'POST', body: JSON.stringify({ signature }),
    }),

  // The approval window (D11): the operator is at the desk for N minutes.
  // While it is open the orchestrator builds sweeps for a Ledger tap and the
  // desk takes a move. Opening again extends it.
  approvalWindow: () => request<{ window: ApprovalWindow | null }>('/api/custody/approval-window'),
  /** Whose money Flagged holds, by depositor (db/0103 held_for): the pay sheet pre-selects one, asks among several. */
  flaggedHeld: () => request<{ holders: Array<{ partySlug: string; partyName: string | null; held: string }> }>('/api/custody/flagged-held'),
  openApprovalWindow: (minutes: number) =>
    request<{ id: number; expiresAt: string }>('/api/custody/approval-window', { method: 'POST', body: JSON.stringify({ minutes }) }),

  /**
   * Queue a move from the clean float: a payout to an approved address, or
   * Hot to Treasury. A custody_sweeps row the orchestrator builds, cards by
   * the destination's class and sends once a Ledger has signed — nothing
   * moves on this call. Refused without an open window.
   */
  // dealId (P-063, db/0111): optional, on a payout to a customer only — the payee's own deal in the Books, not voided;
  // refused deal_invalid · deal_not_a_payout (400), deal_not_found (404), deal_not_payees · deal_voided (409, with a
  // line), deal_unavailable (503, before db/0111).
  proposeMove: (body: { toWalletId: number; amount: string; from?: PayoutSource; requestId?: string; heldFor?: string | null; dealId?: string | null }) =>
    request<{ sweepId: number; status?: MoveStatus; dealId?: string }>('/api/custody/moves', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Release (db/0105, N-099): one deposit KYT sent to Merchant Hot, moved to Hot — a Merchant Hot → Hot move carded
   * 'release', signed by cable like any card. Nothing moves on this call. Refused without an open window; the refusals
   * carry `reason` and, where figures matter, `line`. A press's key is looked up with moveByRequest as a move's is.
   */
  releaseDeposit: (depositId: number, requestId?: string) =>
    request<{ moveId: number; status: MoveStatus }>(`/api/custody/deposits/${depositId}/release`, { method: 'POST', body: JSON.stringify(requestId ? { requestId } : {}) }),
  /** What became of one press, by its key: the row the reservation made, or none NOW (a request still travelling could still reserve). */
  moveByRequest: (requestId: string) =>
    request<{ recorded: false } | { recorded: true; sweepId: number; status: MoveStatus }>(`/api/custody/moves/by-request/${encodeURIComponent(requestId)}`),
  /** Close a press key before a changed payment (db/0093): the row it made, if one exists (recorded, with its status — nothing voided), else voided — nothing under that key can be recorded any more. */
  voidPress: (requestId: string) =>
    request<{ outcome: 'voided' } | { outcome: 'recorded'; sweepId: number; status: MoveStatus }>(`/api/custody/moves/by-request/${encodeURIComponent(requestId)}/void`, { method: 'POST' }),

  // Devices & keys. The key routes are the keys builder's (routes/custody-keys.ts);
  // the shapes are the brief's.
  keys: () => request<{ keys: CustodyKey[] }>('/api/custody/keys'),
  /**
   * Register a Ledger account's public key + chain code; the server derives
   * the first address and refuses a mismatch. Answers the class the account
   * issues and its word (slice E). Refusals, by reason: account_taken (an
   * active key already holds this account), no_registered_device,
   * key_not_this_device (account 0 only), first_address_mismatch,
   * already_registered.
   */
  /** Retire an account key (audit item 17): never deleted; its account is free to register a new Ledger's key, the desk issues nothing more from it. Refused: key_not_found (404). Full access only. */
  retireKey: (id: number, body: { reason?: string } = {}) =>
    request<CustodyKey>(`/api/custody/keys/${id}/retire`, { method: 'POST', body: JSON.stringify(body) }),
  /** The challenge the key proof must carry: minted for this operator, account and key material at this moment (lib/key-proof-challenge.ts). */
  keyProofChallenge: (body: { account: number; publicKey: string; chainCode: string; at: number }) =>
    request<{ challenge: string }>('/api/custody/keys/proof-challenge', { method: 'POST', body: JSON.stringify(body) }),
  registerKey: (body: RegisterKeyBody) =>
    request<{ id: number; account: number; firstAddress: string; lastIndex: number; adopted: string[]; class?: string | null; classWord?: string | null }>('/api/custody/keys', { method: 'POST', body: JSON.stringify(body) }),

  // The move caps (21 Oct 2026, risk 13): per move, per Dubai day; set from the Flow screen.
  /** The caps and what today already carries. Refused: limits_unset (503 — no row; nothing moves until one is set). */
  limits: () => request<MoveLimits>('/api/custody/limits'),
  /**
   * The operator's Received on a payout address's 10 USDT test (db/0098): the test landed and the customer
   * confirmed it to him — the address is whitelisted. Refused: test_not_landed, not_approved (409); not_found (404).
   */
  payoutTestReceived: (id: number) =>
    request<{ stage: 'whitelisted'; receivedAt: string | null; receivedBy: string | null; already?: boolean }>(`/api/custody/whitelist/${id}/received`, { method: 'POST' }),
  /** The TRX floor per float (db/0097): HIS figures on Flow → Caps, or the seeded defaults until he sets them. */
  feeFloors: () => request<FeeFloors>('/api/custody/fee-floors'),
  /** Set the floors, in TRX. Refused: fee_floors_invalid, fee_floors_not_positive, fee_floors_below_minimum (all 400). Full access only. */
  setFeeFloors: (body: Record<FeeFloorClass, string>) =>
    request<FeeFloors>('/api/custody/fee-floors', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Set the caps, in USDT. Refused: limits_invalid, limits_not_positive,
   * day_below_move, limits_absurd (all 400). Full access only.
   * ifUpdatedAt (P-060): the `updatedAt` the screen read. Another save in between (another tab, another operator)
   * refuses this one: 409 limits_changed with `line` and `current` (the caps as they stand); nothing is written.
   */
  setLimits: (body: { moveCeilingUsdt: string; dayCeilingUsdt: string; firstPayoutCeilingUsdt?: string; capsOff?: boolean; secondDevicePayouts?: boolean; sources?: Partial<Record<CappedSource, { moveCeilingUsdt: string; dayCeilingUsdt: string }>>; ifUpdatedAt?: string | null }) =>
    request<MoveLimits>('/api/custody/limits', { method: 'POST', body: JSON.stringify(body) }),

  // Renewals (slice E). All three behind the custody chain; the two writes need full access.
  /** The three renewable classes: the policy, the next due, the active address, whether a key is registered, the addresses retired in the last 90 days with their last reading and any open move. */
  renewals: () => request<{ chain: string; asset: string; renewals: RenewalRow[] }>('/api/custody/renewals'),
  /** Every address a key issued, with its last USDT reading; a failed or stale reading is not read (balance null). */
  keyHoldings: (id: number) => request<KeyHoldings>(`/api/custody/keys/${id}/holdings`),
  /**
   * Set how often a class renews, in days (1..365); the next date stays
   * where it is. Refused: every_days_invalid (400, with min and max),
   * unknown_class (404).
   */
  setRenewalDays: (cls: RenewalClass, everyDays: number) =>
    request<RenewalPolicy>(`/api/custody/renewals/${cls}`, { method: 'POST', body: JSON.stringify({ everyDays }) }),
  /**
   * Make the class due now: the orchestrator's next tick issues the next
   * address and retires the current one. Refused: no_key_for_class (409 —
   * no active key for the class's Ledger account, so nothing could renew),
   * unknown_class (404).
   */
  renewNow: (cls: RenewalClass) =>
    request<RenewalPolicy & { keyAccount: number; keyId: number }>(`/api/custody/renewals/${cls}/renew-now`, { method: 'POST' }),

  /** The deposit addresses the bot issued — one per deal — for the list and its QR codes. */
  depositAddresses: (customer?: string) =>
    request<{ addresses: DepositAddress[] }>(`/api/custody/deposit-addresses${customer ? `?customer=${encodeURIComponent(customer)}` : ''}`),
  /** The one tap (21 Oct 2026): a fresh untagged address, now; the bot's /new is the same statement. */
  newDepositAddress: (note?: string) =>
    request<{ id: number; address: string; chain: string; derivation: string; label: string }>('/api/custody/deposit-addresses', { method: 'POST', body: JSON.stringify(note ? { note } : {}) }),
  /** The customer an address belongs to, given once. */
  tagDepositAddress: (id: number, body: { partySlug: string; dealId?: string }) =>
    request<{ id: number; address: string; partySlug: string; partyName: string | null; dealId: string | null }>(`/api/custody/deposit-addresses/${id}/tag`, { method: 'POST', body: JSON.stringify(body) }),
};
